/* On-the-Day (OTD) MASTER calculator — OTDMaster namespace
   ──────────────────────────────────────────────────────────────────────────
   One engine for the whole on-the-day suite. Look reuses product-studio-calc.css
   plus the OTD item UI ported from the big price-inquiry calculator
   (sections/quote-calculator-v2-full): per item a Material/Size dropdown and an
   "Upgrades" checkbox list with live "+$X" prices and a "from $X" subtitle.
   Pricing is REUSED from window.FEL_PRICING (calcOtd / calcOtdItem) — the big
   calculator and quote-pricing.js are never modified.

   Flow (unchanged shell): Step I guests+date · Step II choose pieces ·
   Step III design tier (Studio $200 / Tailored $300) + already-paid returning ·
   Step IV review + deposit add-to-cart / returning quote.

   Lives at the bottom of the wedding-day-signs-and-stationery page (a Page, so
   the deposit variant GIDs are passed in via section settings, not auto-derived). */
(function () {
  'use strict';
  var P = function () { return window.FEL_PRICING; };

  // ── Item catalogue ─────────────────────────────────────────────────────────
  // Each item declares how its config renders + prices. Options + upgrade prices
  // are resolved live from FEL_PRICING so this file never hard-codes money.
  //   kind:  signage | menus | place | tablenum | vows | service | guestbook |
  //          favour | coaster
  //   qmode: guest (qty = guests) | table (ceil/10) | one (1, signage) |
  //          piece (manual default 1)
  //   ups:   [{ k, label, suffix, gate, mat, price(it,q) }]  → checkbox upgrades
  //
  // Upgrade pricing mirrors calcOtdItem in quote-pricing.js exactly so the live
  // "+$X" label always matches what the engine actually charges.

  var ITEMS = [
    // ── Ceremony ──
    {
      id: 'welcome', group: 'ceremony', name: 'Welcome sign', kind: 'signage', qmode: 'one',
      desc: 'A statement piece designed to welcome your guests and set the visual tone for your day.',
      ups: signageUps('welcome')
    },
    {
      id: 'vows', group: 'ceremony', name: 'Vow books', kind: 'vows', qmode: 'one',
      sub: 'from $45/pair',
      oneNote: 'A beautifully matched pair of hand-bound vow books, tailored to your chosen suite.',
      ups: [
        { k: 'foil', label: 'Metallic Foil Accent', price: function (it) { var e = vowEntry(it); return e && e.foil ? e.foil : null; } }
      ],
      ribbon: true
    },
    {
      id: 'service', group: 'ceremony', name: 'Church booklets & programs', kind: 'service', qmode: 'guest',
      sub: 'booklets or programs · from $4/ea',
      desc: 'Guide your guests beautifully through the ceremony layout with a thoughtfully formatted order of service.',
      ups: [
        { k: 'foil', label: 'Metallic Foil Accent', suffix: '/ea', price: function (it) { return serviceFoil(it); } }
      ],
      ribbon: true
    },

    // ── Reception (order: seating · bar · menus · place · table numbers · small signage) ──
    {
      id: 'seating', group: 'reception', name: 'Seating chart', kind: 'signage', qmode: 'one',
      desc: 'A display piece designed to seamlessly guide your guests to their respective tables.',
      ups: signageUps('seating')
    },
    {
      id: 'bar', group: 'reception', name: 'Bar sign', kind: 'signage', qmode: 'piece',
      sub: 'one or two · from $20',
      desc: 'A refined tabletop addition detailing your signature curation of event beverages.',
      ups: signageUps('bar')
    },
    {
      id: 'menus', group: 'reception', name: 'Personalised menus', kind: 'menus', qmode: 'guest',
      sub: 'personalised with guest names · from $6/ea',
      desc: 'A curated collection of reception menus, beautifully tailored to your tablescape.',
      ups: [
        { k: 'foil', label: 'Metallic Foil Accent', suffix: '/ea', gate: 'not-fabric', price: function (it) { return menuFoil(it); } },
        { k: 'dsp', label: 'Double-Sided Printing', suffix: '/ea', gate: 'menu-flat', price: function () { return ma().doubleSided; } },
        { k: 'cut', label: 'Custom Wave or Arch Die-Cut', suffix: '/ea', gate: 'not-fabric', price: function () { return ma().cutToShape; } }
      ],
      ribbon: true
    },
    {
      id: 'place', group: 'reception', name: 'Place cards', kind: 'place', qmode: 'guest',
      sub: 'from $3.50/ea',
      desc: 'Personalise each guest’s setting with beautifully crafted place cards, tailored to match your tablescape.',
      ups: [
        { k: 'foil', label: 'Metallic Foil Accent', suffix: '/ea', gate: 'place-not-coaster', price: function () { return pa().foil40_100x50; } },
        { k: 'mirror', label: 'Mirror acrylic upgrade', suffix: '/ea', gate: 'place-coaster', price: function () { return pa().coasterMirror; } }
      ]
    },
    {
      id: 'tablenum', group: 'reception', name: 'Table numbers', kind: 'tablenum', qmode: 'table',
      sub: 'from $10.50/ea · ~1 per 10 guests',
      desc: 'Clearly guide your guests to their seating with beautifully formatted table numbers that complement your floral and table styling.',
      ups: [
        { k: 'dsp', label: 'Double-Sided Printing', suffix: '/ea', gate: 'card', price: function () { return tnAdd().dsp; } },
        { k: 'cut', label: 'Custom Cut to Shape', suffix: '/ea', gate: 'card', price: function () { return tnAdd().cut; } },
        { k: 'foil', label: 'Metallic Foil Accent', suffix: '/ea', gate: 'card', price: function () { return tnAdd().foil; } },
        { k: 'stand', label: 'Matching Base Stand / Kickstand', suffix: '/ea', gate: 'card', price: function () { return tnAdd().stand; } },
        { k: 'acut', label: 'Custom Shape', gate: 'acrylic', priceText: '+ free for you', price: function () { return 0; } },
        { k: 'afoil', label: 'Metallic Foil Accent', suffix: '/ea', gate: 'acrylic', price: function () { return 25; } },
        { k: 'layers', label: 'Layered Numbers', note: 'one-off', suffix: '', gate: 'acrylic', price: function () { return tnAcr().layers; } }
      ]
    },
    {
      id: 'small-c', group: 'reception', name: 'Small signage', kind: 'signage', qmode: 'piece',
      sub: 'ceremony or reception · European cardstock or board · from $15',
      desc: 'Elegant, small-format tabletop signs designed for your guest book station, card table, or directional needs.',
      ups: signageUps('small-c')
    },

    // ── Keepsakes & favours ──
    {
      id: 'guestbook', group: 'keepsakes', name: 'Guest book', kind: 'guestbook', qmode: 'one',
      sub: 'acrylic · from $230',
      desc: 'Featuring hand-bound inner pages and premium acrylic covers, expertly tailored to preserve your guests’ sentiments.',
      ups: [
        { k: 'foil', label: 'Foil accent', price: function (it) { var e = gbEntry(it); return e && e.foil ? e.foil : null; } }
      ]
    },
    {
      id: 'coaster', group: 'keepsakes', name: 'Acrylic coasters', kind: 'coaster', qmode: 'guest',
      sub: 'frosted acrylic place-card coasters · from $9.20/ea',
      ups: [
        { k: 'mirror', label: 'Mirror acrylic upgrade', suffix: '/ea', price: function () { return pa().coasterMirror; } }
      ]
    },
    {
      id: 'favour', group: 'keepsakes', name: 'Gift tags', kind: 'favour', qmode: 'guest',
      sub: 'custom tags · from $2.80/ea',
      desc: 'Delicate, customised tags designed to attach seamlessly to your event favours or gifts.',
      ups: [
        { k: 'foil', label: 'Foil accent', suffix: '/ea', price: function () { return fav().foil; } },
        { k: 'cut', label: 'Cut to shape', suffix: '/ea', price: function () { return fav().cut; } }
      ],
      ribbon: true
    },
    {
      id: 'customdecals', group: 'keepsakes', name: 'Custom Decals & Stickers', kind: 'customdecals',
      qmode: 'none', onRequest: true, sub: 'on request'
    }
  ];

  var GROUPS = { ceremony: 'Ceremony Essentials', reception: 'Reception', keepsakes: 'Keepsakes & favours' };
  // Stickers/decals are now the on-request "Custom Decals & Stickers" accordion item — no separate request row.
  var REQUEST = [];

  // Signage upgrades (welcome / seating / bar / small-c). Prices resolved live
  // from OTD_SIGNAGE for the current material+size (rigid) or flat fabric rates.
  function signageUps(id) {
    var rigid = [
      { k: 'cut', label: 'Custom Cut to Shape', mat: 'rigid', price: function (it) { return sigSizeUp(it, 'cut'); } },
      { k: 'stand', label: 'Backing Kickstand for Display', mat: 'rigid', price: function (it) { return sigSizeUp(it, 'stand'); } },
      { k: 'foil', label: 'Metallic Finish', note: (id === 'seating') ? 'for headings & main details' : null, mat: 'rigid', price: function (it) { return sigSizeUp(it, 'foil'); } }
    ];
    // Only welcome + seating offer the hanging hardware (per pricing engine)
    if (id === 'welcome' || id === 'seating') {
      rigid.push({ k: 'dispHoles', label: 'Pre-Drilled Hanging Holes', mat: 'rigid', price: function () { return sigRigidUp().dispHoles; } });
      rigid.push({ k: 'hangRibbon', label: 'Satin 5cm Ribbon for Hanging', mat: 'rigid', price: function () { return sigRigidUp().hangRibbon; } });
    }
    var fabric = [
      { k: 'eyelets', label: 'Eyelets', mat: 'fabric', price: function () { return sigFabUp().eyelets; } },
      { k: 'hemming', label: 'Linen hemming', mat: 'fabric', price: function () { return sigFabUp().linenHemming; } },
      { k: 'loopHem', label: 'Loop hem', mat: 'fabric', price: function () { return sigFabUp().loopHem; } },
      { k: 'ribbon', label: '5cm ribbon bow', mat: 'fabric', price: function () { return sigFabUp().ribbon5cmBow; } }
    ];
    return rigid.concat(fabric);
  }

  // ── pricing-data shortcuts (always re-read; FEL_PRICING never mutated) ───────
  function ma() { return P().OTD_MENUS.addons; }
  function pa() { return P().OTD_PLACECARDS.addons; }
  function fav() { return P().OTD_FAVOURS.addons; }
  function tnAdd() { return P().OTD_TABLENUMBERS.cardAddons; }
  function tnAcr() { return P().OTD_TABLENUMBERS.acrylicAddons; }
  function sigRigidUp() { return P().OTD_SIGNAGE.rigidUp; }
  function sigFabUp() { return P().OTD_SIGNAGE.fabricUp; }
  function vowEntry(it) { return P().OTD_VOWBOOKS[it.format]; }
  function gbEntry(it) { return P().OTD_GUESTBOOK[it.format]; }

  function sigMatKey(material) {
    if (!material) return null;
    return (material.indexOf('fabric') >= 0) ? 'fabric' : material;
  }
  // Live cut/stand/foil price for the current signage material+size (rigid only)
  function sigSizeUp(it, key) {
    var sig = P().OTD_SIGNAGE; var mk = sigMatKey(it.material);
    if (!mk || mk === 'fabric') return null;
    var sd = sig[mk] && sig[mk][it.size];
    return (sd && sd[key]) ? sd[key] : null;
  }
  function menuFoil(it) {
    var m = ma();
    if (it.format === 'folded' || it.format === 'folded-a5') return m.foldedFoilSingle;
    if (it.format === 'triangle') return m.triangleFoilSingle;
    return m.foilSingle; // "from $5/ea"
  }
  function serviceFoil(it) {
    var f = it.format || '';
    if (f.indexOf('cover-') === 0) return P().OTD_BOOKLETS.addons.foil;
    var pa2 = P().OTD_PROGRAMS.addons;
    return f === 'trifold' ? pa2.foilTrifold : pa2.foilFan;
  }

  var cfg = { depSuiteGid: '', depIndivGid: '', feeSuite: 200, feeIndiv: 300, cartBehavior: 'cart', drawerSelector: '', codes: [] };

  var state = {
    step: 1, openItem: null, guests: 0, date: '', tier: 'suite', prepaid: false, codeOK: false, designCode: '',
    singlePiece: null, // set from ?piece=<id> on a collection product page → single-piece mode ($100 design)
    items: {} // id -> { on, format, material, size, qty, qtyManual, ribbon, ups{}, customized }
  };
  ITEMS.forEach(function (d) {
    state.items[d.id] = {
      on: false, format: null, material: null,
      size: null, qty: 0, qtyManual: false, ribbon: 'none', ups: {}, customized: false
    };
  });
  var request = {}; REQUEST.forEach(function (r) { request[r.id] = false; });

  // 5-step flow: I day · II design · III select · IV configure · V review.
  var STEPS = ['Your day', 'Design', 'Choose your pieces', 'Configure', 'Review'];
  var STEP_DESIGN = 2, STEP_SELECT = 3, STEP_CONFIGURE = 4, STEP_REVIEW = 5;
  var firstRender = true, lastTotal = 0;

  var $ = function (id) { return document.getElementById(id); };
  var root = function () { return document.querySelector('.otd-master'); };
  function fmt(n) { return '$' + (Math.round(n * 100) / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  function fmt0(n) { return '$' + Math.round(n).toLocaleString('en-AU'); }
  function fmtMoney(n) { return '$' + (Math.round(n * 100) / 100).toFixed(2); } // AU "$20.00" for dropdown increments
  function prettyDate(d) { if (!d) return ''; var p = String(d).split('-'); if (p.length !== 3) return d; var mo = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']; return (+p[2]) + ' ' + (mo[+p[1] - 1] || '') + ' ' + p[0]; }
  function fmtUp(n) { // compact "+$X" for upgrade labels
    if (n == null) return '—';
    var s = (Math.round(n * 100) / 100).toString();
    if (s.indexOf('.') >= 0) s = s.replace(/0+$/, '').replace(/\.$/, '');
    return '$' + s;
  }
  function tables() { return Math.max(1, Math.ceil((state.guests || 0) / 10)); }
  function designFee() { if (state.singlePiece) return 100; if (state.bundleItems) return 150; return (state.prepaid || state.codeOK) ? 0 : (state.tier === 'indiv' ? cfg.feeIndiv : cfg.feeSuite); }
  function defOf(id) { for (var i = 0; i < ITEMS.length; i++) if (ITEMS[i].id === id) return ITEMS[i]; return null; }

  // ── smart defaults (sensible standard so a baseline price is always > $0) ────
  // Signage: { material, size }. Other kinds: format/style string.
  var DEFAULTS = {
    welcome:  { material: 'board',     size: 'A2-long' },
    seating:  { material: 'board',     size: 'A1' },
    bar:      { material: 'cardstock', size: 'A4' },
    'small-c':{ material: 'cardstock', size: 'A6' },
    menus:    'flat-sm',
    place:    'flat',
    tablenum: 'card-sm',
    vows:     'a6-std',
    service:  'trifold',
    guestbook:'a5',
    favour:   'tag50'
    // coaster: qty only — no format
  };
  function defaultFormat(def) {
    var d = DEFAULTS[def.id];
    return (typeof d === 'string') ? d : null;
  }
  function defaultMaterial(def) {
    if (def.kind !== 'signage') return null;
    var d = DEFAULTS[def.id];
    if (d && d.material) return d.material;
    var mats = materialsFor(def.id);
    return mats.length ? mats[0] : null;
  }
  // Apply smart defaults on selection — only fills EMPTY fields (never clobbers
  // a returning / already-chosen value). Guarantees a baseline price > $0.
  function applyDefaults(def) {
    var it = state.items[def.id], d = DEFAULTS[def.id];
    if (def.kind === 'signage') {
      if (!it.material) it.material = (d && d.material) ? d.material : (materialsFor(def.id)[0] || null);
      if (!it.size) {
        var wanted = (d && d.size) ? d.size : null;
        var avail = sizesFor(def.id, it.material);
        it.size = (wanted && avail.indexOf(wanted) >= 0) ? wanted : (avail[0] || null);
      }
    } else if (def.kind !== 'coaster') {
      if (!it.format) it.format = (typeof d === 'string') ? d : (formatsFor(def)[0] || null);
    }
  }

  // ── options from FEL_PRICING ─────────────────────────────────────────────────
  function formatsFor(def) {
    var pr = P(); if (!pr) return [];
    switch (def.kind) {
      case 'menus': return Object.keys(pr.OTD_MENUS.base);
      case 'place': return Object.keys(pr.OTD_PLACECARDS.base);
      case 'tablenum': return Object.keys(pr.OTD_TABLENUMBERS).filter(function (k) { return ['cardAddons', 'acrylicAddons'].indexOf(k) < 0; });
      case 'vows': return Object.keys(pr.OTD_VOWBOOKS).filter(function (k) { return k !== 'ribbon'; });
      case 'service': return ['trifold', 'fan', 'cover-only', 'cover-matte'];
      case 'guestbook': return Object.keys(pr.OTD_GUESTBOOK);
      case 'favour': return Object.keys(pr.OTD_FAVOURS.base);
      default: return [];
    }
  }
  function materialsFor(id) { var pr = P(); var a = pr && pr.SIGN_ALLOWED[id]; return a ? Object.keys(a) : []; }
  function sizesFor(id, material) { var pr = P(); var a = pr && pr.SIGN_ALLOWED[id]; return (a && a[material]) ? a[material] : []; }
  function sizeLabel(s) { var pr = P(); return (pr && pr.SIZE_LABELS[s]) || s; }

  // Friendly labels for format/material keys (fall back to the key)
  var LBL = {
    'flat-sm': 'Flat 100×200', 'flat-lg': 'Flat 120×200 / A5', 'round': 'Round', 'folded': 'Folded 120×180',
    'folded-a5': 'Folded A5', 'fabric-sm': 'Fabric 120×200', 'fabric-lg': 'Fabric 130×300', 'triangle': 'Triangle',
    'cutshape': 'Sculptural Flat', 'acrylic': 'Acrylic', 'coaster': 'Coaster Acrylic', 'tag100': 'Tag 100×50', 'tag50': 'Tag 50×50', 'glass': 'Glass place card', 'flat': 'The Petite Flat',
    'card-sm': 'Cardstock 100×150', 'card-classic': 'Cardstock 100×200', 'card-md': 'Cardstock 148×210',
    'fab-std': 'Fabric 30×1m', 'fab-grand': 'Fabric 30×1.5m', 'acrylic-sm': 'The Minimalist Acrylic', 'acrylic-md': 'The Editorial Acrylic', 'acrylic-lg': 'Acrylic A5',
    'a6-std': 'A6 Standard', 'a5-std': 'A5 Standard', 'a6-hm': 'A6 Handmade', 'a5-hm': 'A5 Handmade',
    'cover-only': 'Booklet A5 (cover only)', 'cover-matte': 'Booklet A5 (cover & matte)', 'trifold': 'Tri-Fold Program', 'fan': 'Fan Program',
    'a5': 'A5 acrylic', 'a4': 'A4 acrylic',
    'cardstock': 'European Cardstock', 'board': 'Board', 'acrylic-m': 'Acrylic', 'fabric-linen': 'Fabric (linen)', 'fabric-linen-narrow': 'Fabric (linen)'
  };
  function lbl(k) { return LBL[k] || k; }
  function matLabel(m) { return m === 'acrylic' ? 'Acrylic' : lbl(m); }

  // ── signage material image swatches (macro texture photos) ───────────────────
  var MAT_IMG = {
    'board': 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/Foamboard_sign_Felicitations.png?v=1782376138',
    'acrylic': 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/Acrylic_signage_Felicitations.png?v=1782376138',
    'cardstock': 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/European_cardstock_Felicitations.png?v=1782379321',
    'fabric-linen': 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/Fabric_sign_Felicitations.png?v=1782376138',
    'fabric-linen-narrow': 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/Fabric_sign_Felicitations.png?v=1782376138'
  };
  // Swatch tile label: fabric keys collapse to "Fabric"; others use matLabel.
  function matSwatchLabel(m) { return (m && m.indexOf('fabric') >= 0) ? 'Fabric' : matLabel(m); }

  // ── Vow books: material (European/Handmade) + size compose the FEL key ───────
  // FEL key = "<size>-<material>" (a6-std / a5-std / a6-hm / a5-hm). std=European, hm=Handmade.
  var VOW_IMG = {
    'std': 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/European_cardstock_Felicitations.png?v=1782379321',
    'hm': 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/Handmade_Cotton_Paper_Felicitations.png?v=1782378739'
  };
  var VOW_MAT_LABEL = { 'std': 'European Cardstock', 'hm': 'Handmade Cardstock' };
  var VOW_SIZE_LABEL = { 'a6': 'A6 Pocket (105×148mm)', 'a5': 'A5 Standard (148×210mm)' };
  function vowMatOf(it) { return ((it.format || 'a5-std').split('-')[1]) || 'std'; }
  function vowSizeOf(it) { return ((it.format || 'a5-std').split('-')[0]) || 'a5'; }

  // ── Church booklets & programs: 3 image cards; "Church Booklet" reveals a
  // Cover-Only / Full-Booklet-with-Pages segmented sub-selection (cover-only / cover-matte). ──
  var SERVICE_CARDS = [
    { v: 'booklet', label: 'Church Booklet', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-booklet-wedding-program-classic-002.webp?v=1767779028' },
    { v: 'trifold', label: 'Tri-fold Program', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-booklet-tri-fold-program-003.webp?v=1767779028' },
    { v: 'fan',     label: 'Ceremony Fan', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/Maryke_and_Dean_-_invitations_and_on_the_day_stationery_with_gold_foil_by_Felicitations.webp?v=1767779028' }
  ];
  function serviceCardOf(it) { var f = it.format || 'cover-only'; return (f === 'trifold' || f === 'fan') ? f : 'booklet'; }
  function serviceCardRow(def, it) {
    var cur = serviceCardOf(it);
    var row = el('div', 'otd-cfg-row otd-mat-row');
    row.appendChild(el('span', 'otd-cfg-label', 'Format'));
    var grid = el('div', 'otd-mat-grid'); grid.dataset.serviceGrid = def.id;
    SERVICE_CARDS.forEach(function (c) {
      var sel = (c.v === cur);
      var tile = el('button', 'otd-mat-tile' + (sel ? ' on' : ''));
      tile.type = 'button'; tile.dataset.serviceCard = def.id + '.' + c.v;
      tile.setAttribute('aria-pressed', sel ? 'true' : 'false');
      tile.innerHTML = '<span class="otd-mat-img"><img src="' + c.img + '" alt="" loading="lazy"></span><span class="otd-mat-label">' + c.label + '</span>';
      grid.appendChild(tile);
    });
    row.appendChild(grid); return row;
  }
  // Sub-selection shown only when the Church Booklet card is active.
  function serviceCoverRow(def, it) {
    var row = el('div', 'otd-cfg-row'); row.dataset.serviceCoverRow = def.id;
    if (serviceCardOf(it) !== 'booklet') { row.hidden = true; return row; }
    row.appendChild(el('span', 'otd-cfg-label', 'Pages'));
    var seg = el('div', 'seg seg-inset'); seg.dataset.serviceCoverSeg = def.id;
    [['cover-only', 'Cover Only'], ['cover-matte', 'Full Booklet with Pages']].forEach(function (o) {
      var b = el('button', 'seg-opt' + (it.format === o[0] ? ' on' : ''), o[1]);
      b.type = 'button'; b.dataset.serviceCover = def.id + '.' + o[0]; seg.appendChild(b);
    });
    row.appendChild(seg); return row;
  }
  // Place cards → 4 image swatches (Petite Flat / Classic Tent / Sculptural Flat / Acrylic Coaster).
  var PLACE_CARDS = [
    { v: 'flat',     label: 'Flat', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-place-card-086.webp?v=1765300754' },
    { v: 'folded',   label: 'Tent', img: 'https://felicitations.com.au/cdn/shop/files/Rivera_Garden_Soiree_-_Chambre_Estate_Styled_Shoot536.webp?v=1749278199&width=1000' },
    { v: 'cutshape', label: 'Sculptural', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-place-card-custom-018.webp?v=1765300750' },
    { v: 'coaster',  label: 'Acrylic', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-coaster-018.webp?v=1750507612' }
  ];
  // Generic image-swatch row for a format-based item (service / place). gridClass optional.
  function fmtSwatchRow(def, it, cards, gridClass) {
    var row = el('div', 'otd-cfg-row otd-mat-row');
    row.appendChild(el('span', 'otd-cfg-label', 'Format'));
    var grid = el('div', 'otd-mat-grid' + (gridClass ? ' ' + gridClass : '')); grid.dataset.fmtGrid = def.id;
    cards.forEach(function (c) {
      var sel = (c.v === it.format);
      var tile = el('button', 'otd-mat-tile' + (sel ? ' on' : ''));
      tile.type = 'button'; tile.dataset.itemFmtsw = def.id + '.' + c.v;
      tile.setAttribute('aria-pressed', sel ? 'true' : 'false');
      tile.innerHTML = '<span class="otd-mat-img"><img src="' + c.img + '" alt="" loading="lazy"></span>' +
        '<span class="otd-mat-label">' + c.label + '</span>';
      grid.appendChild(tile);
    });
    row.appendChild(grid);
    return row;
  }

  // ── Menus: two-tier (Material segmented → conditional format swatches) ────────
  var MENU_CARDS = {
    cardstock: [
      { v: 'flat-sm', label: 'The Slim Flat (100×200mm)', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-menu-place-card-019.webp?v=1776265122' },
      { v: 'flat-lg', label: 'The Editorial Flat (A5)', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-menu-wedding-reception-082.webp?v=1766763994' },
      { v: 'round',   label: 'The Contemporary Round', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-menu-round-063.webp' },
      { v: 'folded',  label: 'The Classic Folded', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations_wedding_invitation_stationery_perth_australia_Positano_0827.webp?v=1766763994' }
    ],
    fabric: [
      { v: 'fabric-sm', label: 'Premium Linen (120×200mm)', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-menu-personalised-guest-linen-054.webp?v=1766763994' },
      { v: 'fabric-lg', label: 'Statement Linen (130×300mm)', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-menu-personalised-guest-linen-054.webp?v=1766763994' }
    ]
  };
  function menuMatOf(it) { return (it.format && it.format.indexOf('fabric') === 0) ? 'fabric' : 'cardstock'; }
  // Menu material photos: Cardstock = European cardstock (vow texture), Fabric = welcome-sign linen.
  var MENU_MAT_IMG = {
    cardstock: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/European_cardstock_Felicitations.png?v=1782379321',
    fabric: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/Fabric_sign_Felicitations.png?v=1782376138'
  };
  // Tier 1 — Cardstock / Fabric as IMAGE swatches.
  function menuMatRow(def, it) {
    var cur = menuMatOf(it);
    var row = el('div', 'otd-cfg-row otd-mat-row');
    row.appendChild(el('span', 'otd-cfg-label', 'Material'));
    var grid = el('div', 'otd-mat-grid otd-mat-grid-2'); grid.dataset.menuMatSeg = def.id;
    [['cardstock', 'European Cardstock'], ['fabric', 'Fabric']].forEach(function (o) {
      var sel = (cur === o[0]);
      var tile = el('button', 'otd-mat-tile' + (sel ? ' on' : ''));
      tile.type = 'button'; tile.dataset.menuMat = def.id + '.' + o[0];
      tile.setAttribute('aria-pressed', sel ? 'true' : 'false');
      tile.innerHTML = '<span class="otd-mat-img"><img src="' + (MENU_MAT_IMG[o[0]] || '') + '" alt="" loading="lazy"></span>' +
        '<span class="otd-mat-label">' + o[1] + '</span>';
      grid.appendChild(tile);
    });
    row.appendChild(grid); return row;
  }
  // Tier 2 — format BUTTONS for the active material (4 cardstock / 2 fabric).
  function fmtButtonRow(def, it, cards) {
    var row = el('div', 'otd-cfg-row');
    row.appendChild(el('span', 'otd-cfg-label', 'Format'));
    var wrap = el('div', 'scalc-btns scalc-fixed otd-fmt-btns'); wrap.dataset.fmtGrid = def.id;
    cards.forEach(function (c) {
      var b = el('button', 'scalc-opt' + (c.v === it.format ? ' on' : ''), c.label);
      b.type = 'button'; b.dataset.itemFmtsw = def.id + '.' + c.v;
      wrap.appendChild(b);
    });
    row.appendChild(wrap); return row;
  }
  // Connected segmented control for a format-based item (e.g. guest book A5 / A4).
  function segFmtRow(def, it, labelTxt, opts) {
    var row = el('div', 'otd-cfg-row');
    row.appendChild(el('span', 'otd-cfg-label', labelTxt));
    var seg = el('div', 'seg seg-inset'); seg.dataset.fmtGrid = def.id;
    opts.forEach(function (o) {
      var b = el('button', 'seg-opt' + (o.v === it.format ? ' on' : ''), o.label);
      b.type = 'button'; b.dataset.itemFmtsw = def.id + '.' + o.v; seg.appendChild(b);
    });
    row.appendChild(seg); return row;
  }
  function menuFmtRow(def, it) {
    var cards = MENU_CARDS[menuMatOf(it)], pr = P(), tier = pr.tierOTD(state.guests || 0);
    var bases = cards.map(function (c) { var a = pr.OTD_MENUS.base[c.v]; return a ? a[tier] : null; });
    var valid = bases.filter(function (b) { return b != null; });
    var min = valid.length ? Math.min.apply(null, valid) : 0;
    var opts = cards.map(function (c, i) { var inc = (bases[i] != null) ? bases[i] - min : 0; return { v: c.v, label: c.label + (inc > 0.001 ? ' (+ ' + fmtMoney(inc) + ')' : '') }; });
    var row = selectRow('Format', 'data-menu-fmt', def.id, opts, it.format || '');
    row.dataset.menuFmtRow = def.id;
    return row;
  }
  // Gift tags: 2 image swatches (only one tag photo so far — both tiles reuse it).
  var TAG_IMG = 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-tag-039.webp?v=1765300775';
  var FAV_CARDS = [
    { v: 'tag50', label: 'Petite Tag (50×50mm)', img: TAG_IMG },
    { v: 'tag100', label: 'Classic Tag (100×50mm)', img: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/felicitations-wedding-stationery-behind-scene-0102.webp?v=1750515294' }
  ];

  // ── Table numbers: material image swatches → conditional size dropdown ────────
  // The FEL format key IS the size value (card-sm / fab-std / acrylic-sm …); material
  // is derived from the prefix. Material swatch resets format to that material's default.
  var TNUM_MAT_IMG = {
    cardstock: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/European_cardstock_Felicitations.png?v=1782379321',
    acrylic: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/Acrylic_signage_Felicitations.png?v=1782376138',
    fabric: 'https://cdn.shopify.com/s/files/1/0700/1669/6516/files/Fabric_sign_Felicitations.png?v=1782376138'
  };
  var TNUM_SIZES = {
    cardstock: [
      { v: 'card-sm', label: 'Standard Petite (100×150mm)' },
      { v: 'card-classic', label: 'Elegant Tall (100×200mm)' },
      { v: 'card-md', label: 'A5 Editorial (148×210mm)' }
    ],
    fabric: [
      { v: 'fab-std', label: 'Classic Banner (30cm × 1m)' },
      { v: 'fab-grand', label: 'Extended Banner (30cm × 1.5m)' }
    ],
    acrylic: [
      { v: 'acrylic-sm', label: 'The Minimalist Acrylic' },
      { v: 'acrylic-md', label: 'The Editorial Acrylic' }
    ]
  };
  function tnumMatOf(it) { var f = it.format || 'card-classic'; return f.indexOf('fab') === 0 ? 'fabric' : (f.indexOf('acrylic') === 0 ? 'acrylic' : 'cardstock'); }
  function tnumMaterialRow(def, it) {
    var cur = tnumMatOf(it);
    var row = el('div', 'otd-cfg-row otd-mat-row');
    row.appendChild(el('span', 'otd-cfg-label', 'Material'));
    var grid = el('div', 'otd-mat-grid'); grid.dataset.tnumGrid = def.id;
    ['cardstock', 'acrylic', 'fabric'].forEach(function (m) {
      var sel = (m === cur);
      var tile = el('button', 'otd-mat-tile' + (sel ? ' on' : ''));
      tile.type = 'button'; tile.dataset.tnumMat = def.id + '.' + m;
      tile.setAttribute('aria-pressed', sel ? 'true' : 'false');
      tile.innerHTML = '<span class="otd-mat-img"><img src="' + (TNUM_MAT_IMG[m] || '') + '" alt="" loading="lazy"></span>' +
        '<span class="otd-mat-label">' + (m === 'fabric' ? 'Fabric' : (m === 'acrylic' ? 'Acrylic' : 'European Cardstock')) + '</span>';
      grid.appendChild(tile);
    });
    row.appendChild(grid);
    return row;
  }
  function tnumSizeRow(def, it) {
    var cards = TNUM_SIZES[tnumMatOf(it)], pr = P();
    var bases = cards.map(function (c) { var e = pr.OTD_TABLENUMBERS[c.v]; return e ? e.base : null; });
    var valid = bases.filter(function (b) { return b != null; });
    var min = valid.length ? Math.min.apply(null, valid) : 0;
    var opts = cards.map(function (c, i) { var inc = (bases[i] != null) ? bases[i] - min : 0; return { v: c.v, label: c.label + (inc > 0.001 ? ' (+ ' + fmtMoney(inc) + ')' : '') }; });
    var row = selectRow('Size', 'data-tnum-size', def.id, opts, it.format || '');
    row.dataset.tnumSizeRow = def.id;
    return row;
  }

  // ── "from $X" subtitle (cheapest configured price for the item) ──────────────
  function fromSub(def) {
    if (def.sub) return def.sub; // explicit subtitle wins
    var pr = P(); if (!pr) return '';
    var cheapest = function (arr) { return (arr && arr.length) ? arr[arr.length - 1] : null; };
    if (def.kind === 'signage') {
      // cheapest base across allowed material/size combos
      var min = null, allowed = pr.SIGN_ALLOWED[def.id] || {};
      Object.keys(allowed).forEach(function (mat) {
        var mk = sigMatKey(mat);
        allowed[mat].forEach(function (sz) {
          var e = pr.OTD_SIGNAGE[mk] && pr.OTD_SIGNAGE[mk][sz];
          if (!e) return;
          var b = (typeof e.base === 'number') ? e.base : (typeof e.linen === 'number' ? e.linen : null);
          if (b != null && (min == null || b < min)) min = b;
        });
      });
      return min != null ? 'from ' + fmt0(min) : '';
    }
    return '';
  }

  // ── per-item quantity + pricing (reuse FEL_PRICING; coaster manual) ──────────
  function itemQty(def, it) {
    if (it.qtyManual) return it.qty || 0;
    // Church booklets & tri-fold programs: ~1 per couple/family (60%); fan = 1 per guest.
    if (def.id === 'service') return (it.format === 'fan') ? (state.guests || 0) : Math.ceil((state.guests || 0) * 0.6);
    if (def.qmode === 'guest') return state.guests || 0;
    if (def.qmode === 'table') return tables();
    if (def.qmode === 'one') return 1;
    return it.qty || 1; // piece default 1
  }
  // Build the single-item config object FEL_PRICING.calcOtdItem expects.
  function priceCfg(def, it, q) {
    return {
      on: true, qty: q,
      format: it.format, style: it.format, material: it.material, size: it.size,
      ups: it.ups || {}, ribbon: it.ribbon || 'none'
    };
  }
  function itemPrice(def) {
    var pr = P(), it = state.items[def.id]; if (!pr || !it.on) return 0;
    if (def.onRequest) return 0; // quoted separately
    var q = itemQty(def, it);
    if (def.id === 'coaster') {
      if (!q) return 0;
      var c = pr.OTD_PLACECARDS.base.coaster; if (!c) return 0;
      var t = c[pr.tierOTD(q)] * q;
      if (it.ups && it.ups.mirror) t += pr.OTD_PLACECARDS.addons.coasterMirror * q;
      return t;
    }
    var items = {}; items[def.id] = priceCfg(def, it, q);
    var base; try { base = pr.calcOtd({ otd: { on: true, items: items } }); } catch (e) { return 0; }
    // Acrylic table-number foil is a studio rate not modelled in the shared engine.
    if (def.id === 'tablenum' && tnumMatOf(it) === 'acrylic' && it.ups && it.ups.afoil) base += 25 * q;
    return base;
  }
  function printTotal() { var t = 0; ITEMS.forEach(function (d) { if (state.items[d.id].on) t += itemPrice(d); }); return t; }
  function itemReady(def) {
    var it = state.items[def.id]; if (!it.on) return false;
    if (def.onRequest) return true; // on-request items need no configuration to proceed
    if (def.kind === 'signage') return !!(it.material && it.size);
    if (def.kind === 'coaster') return itemQty(def, it) > 0;
    return !!it.format;
  }

  // ── upgrade gating (which upgrade checkboxes are visible / valid) ─────────────
  // Mirrors refreshFormatGate + refreshSignageMaterial gating in the big calc.
  function upAllowed(def, it, up) {
    if (up.mat) { // signage rigid/fabric split
      var isFab = it.material && it.material.indexOf('fabric') >= 0;
      if (up.mat === 'rigid' && isFab) return false;
      if (up.mat === 'fabric' && !isFab) return false;
      return true;
    }
    var fmt = String(it.format || '');
    switch (up.gate) {
      case 'not-fabric': return fmt.indexOf('fab') !== 0 && fmt.indexOf('fabric') !== 0;
      case 'menu-flat': return fmt === 'flat-sm' || fmt === 'flat-lg'; // double-sided only for flat menus

      case 'card': return fmt.indexOf('card') === 0;
      case 'acrylic': return fmt.indexOf('acrylic') === 0;
      case 'place-coaster': return fmt === 'coaster';
      case 'place-not-coaster': return fmt !== 'coaster';
      default: return true;
    }
  }
  // Resolve the live upgrade price (or null = not available → row hidden).
  function upPrice(def, it, up) {
    if (!upAllowed(def, it, up)) return null;
    try { return up.price ? up.price(it) : null; } catch (e) { return null; }
  }

  // ── render item cards ────────────────────────────────────────────────────────
  function el(tag, cls, html) { var e = document.createElement(tag); if (cls) e.className = cls; if (html != null) e.innerHTML = html; return e; }
  function buildGroups() {
    // Bundle mode → ONE flat "create your own bundle" checklist (no Ceremony / Reception /
    // Keepsakes split, and no empty group headings). Render every bundle piece, in order,
    // into the first container and hide the rest.
    if (state.bundleItems) {
      ['ceremony', 'reception', 'keepsakes'].forEach(function (g) { var w = $('otdGrp-' + g); if (w) w.innerHTML = ''; });
      var flat = $('otdGrp-ceremony');
      state.bundleItems.forEach(function (id) { var def = defOf(id); if (def && flat) flat.appendChild(itemCard(def)); });
      var rootEl = root();
      if (rootEl) {
        rootEl.querySelectorAll('[data-step="3"] .otd-grp-title').forEach(function (t) { t.style.display = 'none'; });
        ['reception', 'keepsakes'].forEach(function (g) { var w = $('otdGrp-' + g), grp = w && w.closest('.otd-grp'); if (grp) grp.style.display = 'none'; });
      }
      return;
    }
    Object.keys(GROUPS).forEach(function (g) {
      var wrap = $('otdGrp-' + g); if (!wrap) return; wrap.innerHTML = '';
      ITEMS.filter(function (d) { return d.group === g && (!state.bundleItems || state.bundleItems.indexOf(d.id) >= 0); }).forEach(function (def) { wrap.appendChild(itemCard(def)); });
    });
    // Stickers & decals (on request) now live under Keepsakes & favours — not part of a fixed bundle.
    var kp = $('otdGrp-keepsakes');
    if (kp && !state.bundleItems) { REQUEST.forEach(function (r) { kp.appendChild(requestCard(r)); }); }
  }

  // Step II card = selection only. The checkbox just toggles selection; the
  // per-item config lives in Step III, so nothing expands inline here.
  function itemCard(def) {
    var it = state.items[def.id];
    var card = el('div', 'otd-card' + (it.on ? ' on' : ''));
    card.dataset.item = def.id;
    var head = el('label', 'otd-card-head');
    head.innerHTML = '<input type="checkbox" data-item-toggle="' + def.id + '"' + (it.on ? ' checked' : '') +
      '><span class="otd-item-name">' + def.name + '<span class="otd-item-sub">' + fromSub(def) + '</span></span>' +
      '<span class="otd-card-price"></span>';
    card.appendChild(head);
    return card;
  }

  // Native <select> in the studio .sel wrapper (cleaner than maroon buttons).
  function selectRow(labelTxt, dataAttr, id, opts, current) {
    var row = el('div', 'otd-cfg-row');
    row.appendChild(el('span', 'otd-cfg-label', labelTxt));
    var sel = document.createElement('select');
    sel.setAttribute(dataAttr, id);
    opts.forEach(function (o) { var op = new Option(o.label, o.v); if (o.v === current) op.selected = true; sel.appendChild(op); });
    var w = el('div', 'sel'); w.appendChild(sel); row.appendChild(w);
    return row;
  }
  // Signage material picker: image tiles (one per allowed material) instead of a
  // <select>. Clicking a tile routes through setItemMat (resets size + re-renders).
  function materialSwatchRow(def, it) {
    var row = el('div', 'otd-cfg-row otd-mat-row');
    row.appendChild(el('span', 'otd-cfg-label', 'Material'));
    var mats = materialsFor(def.id);
    var gridCls = (mats.length === 3) ? '' : ' otd-mat-grid-2'; // 4 materials (bar) → 2×2; 2 → 2-up; 3 stays 3-up
    var grid = el('div', 'otd-mat-grid' + gridCls); grid.dataset.matGrid = def.id;
    mats.forEach(function (m) {
      var sel = (m === it.material);
      var tile = el('button', 'otd-mat-tile' + (sel ? ' on' : ''));
      tile.type = 'button';
      tile.dataset.itemMatsw = def.id + '.' + m;
      tile.setAttribute('aria-pressed', sel ? 'true' : 'false');
      var img = MAT_IMG[m] || '';
      tile.innerHTML =
        '<span class="otd-mat-img">' + (img ? '<img src="' + img + '" alt="" loading="lazy">' : '') + '</span>' +
        '<span class="otd-mat-label">' + matSwatchLabel(m) + '</span>';
      grid.appendChild(tile);
    });
    row.appendChild(grid);
    return row;
  }
  // Large sizes are Perth pick-up only (matches the big price-inquiry calculator).
  var PICKUP_ONLY = { 'A0': 1, '800x1500': 1, '900x1800': 1, '1100x2100': 1 };
  function sizeRow(def, it) {
    var sizes = sizesFor(def.id, it.material), pr = P(), mk = sigMatKey(it.material);
    var bases = sizes.map(function (s) { var e = pr.OTD_SIGNAGE[mk] && pr.OTD_SIGNAGE[mk][s]; return e ? (typeof e.base === 'number' ? e.base : (typeof e.linen === 'number' ? e.linen : null)) : null; });
    var valid = bases.filter(function (b) { return b != null; });
    var min = valid.length ? Math.min.apply(null, valid) : 0;
    var opts = sizes.map(function (s, i) {
      var inc = (bases[i] != null) ? bases[i] - min : 0;
      return { v: s, label: sizeLabel(s) + (inc > 0.001 ? ' (+ ' + fmtMoney(inc) + ')' : '') + (PICKUP_ONLY[s] ? ' · Perth pick-up only' : '') };
    });
    var row = selectRow('Size', 'data-item-size', def.id, [{ v: '', label: 'Choose a size…' }].concat(opts), it.size || '');
    row.dataset.sizeRow = def.id;
    return row;
  }
  // Vow-book material picker (European vs Handmade) — 2-col image swatches.
  function vowMaterialRow(def, it) {
    var cur = vowMatOf(it);
    var row = el('div', 'otd-cfg-row otd-mat-row');
    row.appendChild(el('span', 'otd-cfg-label', 'Material'));
    var grid = el('div', 'otd-mat-grid otd-mat-grid-2'); grid.dataset.vowGrid = def.id;
    ['std', 'hm'].forEach(function (m) {
      var sel = (m === cur);
      var tile = el('button', 'otd-mat-tile' + (sel ? ' on' : ''));
      tile.type = 'button'; tile.dataset.vowMatsw = def.id + '.' + m;
      tile.setAttribute('aria-pressed', sel ? 'true' : 'false');
      tile.innerHTML = '<span class="otd-mat-img"><img src="' + (VOW_IMG[m] || '') + '" alt="" loading="lazy"></span>' +
        '<span class="otd-mat-label">' + VOW_MAT_LABEL[m] + '</span>';
      grid.appendChild(tile);
    });
    row.appendChild(grid);
    return row;
  }
  function vowSizeRow(def, it) {
    var mat = vowMatOf(it), pr = P(), sizes = ['a6', 'a5'];
    var bases = sizes.map(function (s) { var e = pr.OTD_VOWBOOKS[s + '-' + mat]; return e ? e.base : null; });
    var valid = bases.filter(function (b) { return b != null; });
    var min = valid.length ? Math.min.apply(null, valid) : 0;
    var opts = sizes.map(function (s, i) { var inc = (bases[i] != null) ? bases[i] - min : 0; return { v: s, label: VOW_SIZE_LABEL[s] + (inc > 0.001 ? ' (+ ' + fmtMoney(inc) + ')' : '') }; });
    var row = selectRow('Size', 'data-vow-size', def.id, opts, vowSizeOf(it));
    row.dataset.vowSizeRow = def.id;
    return row;
  }
  function qtyRow(def) {
    var it = state.items[def.id];
    if (def.onRequest) return null;
    if (def.qmode === 'one') return def.desc ? null : el('p', 'scalc-hint', 'One ' + def.name.toLowerCase() + '.');
    var auto = def.qmode === 'guest' ? 'one per guest' : (def.qmode === 'table' ? 'one per table (' + tables() + ')' : '');
    if (def.id === 'service') auto = (it.format === 'fan') ? 'one per guest' : 'about 1 per couple (60%)';
    var g = el('div', 'otd-qty');
    g.innerHTML = '<span class="caps">Quantity</span>' +
      '<input class="qty-input" type="number" min="0" max="3000" data-item-qty="' + def.id + '" value="' + (itemQty(def, it) || '') + '" placeholder="0">' +
      (auto ? '<span class="otd-qty-auto">' + auto + '</span>' : '');
    return g;
  }
  // Upgrades checkbox list (ported "Upgrades" row from the big calc).
  function upsRow(def) {
    var it = state.items[def.id];
    if (!def.ups || !def.ups.length) return null;
    var row = el('div', 'otd-cfg-row ups-row');
    row.appendChild(el('span', 'otd-cfg-label', 'Upgrades'));
    var stack = el('div', 'cb-stack'); stack.dataset.upsFor = def.id;
    def.ups.forEach(function (up) { stack.appendChild(upLabel(def, it, up)); });
    row.appendChild(stack);
    return row;
  }
  function upLabel(def, it, up) {
    var price = upPrice(def, it, up);
    var lab = el('label', 'cb-inline' + (price == null ? ' priceup-na' : ''));
    lab.dataset.up = def.id + '.' + up.k;
    var note = up.note ? ' · <span class="up-note">' + up.note + '</span>' : '';
    var sfx = up.suffix != null ? up.suffix : '';
    var priceTxt = up.priceText != null ? up.priceText : (price == null ? '—' : '+' + fmtUp(price) + sfx);
    lab.innerHTML = '<input type="checkbox" data-item-up="' + def.id + '.' + up.k + '"' + (it.ups[up.k] ? ' checked' : '') + '>' +
      '<span class="cb-text"><em>' + up.label + '</em>' + note + '</span>' +
      '<span class="upgrade-price">' + priceTxt + '</span>';
    return lab;
  }
  // Ribbon segmented control (menus / vows / service / favour).
  function ribbonRow(def) {
    if (!def.ribbon) return null;
    var it = state.items[def.id];
    var row = el('div', 'otd-cfg-row');
    row.appendChild(el('span', 'otd-cfg-label', 'Ribbon'));
    var seg = el('div', 'seg seg-inset'); seg.dataset.ribbonFor = def.id;
    var opts = [['none', 'None'], ['satin', 'Satin · $7'], ['velvet', 'Velvet · $9'], ['silk', 'Silk · $12']];
    opts.forEach(function (o) {
      var b = el('button', 'seg-opt' + ((it.ribbon || 'none') === o[0] ? ' on' : ''), o[1]);
      b.type = 'button'; b.dataset.itemRibbon = def.id + '.' + o[0]; seg.appendChild(b);
    });
    row.appendChild(seg);
    return row;
  }

  // The per-item config controls (selects + qty + upgrades + ribbon).
  function itemConfig(def) {
    var it = state.items[def.id];
    var box = el('div', 'otd-cfg');
    if (def.desc) box.appendChild(el('p', 'otd-cfg-note', def.desc)); // elegant description on top
    if (def.kind === 'signage') {
      box.appendChild(materialSwatchRow(def, it)); // image tiles (no dropdown)
      box.appendChild(sizeRow(def, it));
    } else if (def.kind === 'vows') {
      if (def.oneNote) box.appendChild(el('p', 'otd-cfg-note', def.oneNote)); // description first
      box.appendChild(vowMaterialRow(def, it)); // European / Handmade image swatches
      box.appendChild(vowSizeRow(def, it));     // A6 / A5 dropdown
    } else if (def.kind === 'service') {
      box.appendChild(serviceCardRow(def, it));  // Church Booklet / Tri-fold / Fan image cards
      box.appendChild(serviceCoverRow(def, it)); // Cover Only / Full Booklet (only when Church Booklet)
    } else if (def.kind === 'menus') {
      box.appendChild(menuMatRow(def, it)); // Tier 1: European Cardstock / Fabric
      box.appendChild(menuFmtRow(def, it)); // Tier 2: conditional format dropdown
      box.appendChild(el('p', 'scalc-hint otd-incl-note', 'Individual Guest Name Printing Included • Complimentary'));
    } else if (def.kind === 'tablenum') {
      box.appendChild(tnumMaterialRow(def, it)); // Cardstock / Acrylic / Fabric image swatches
      box.appendChild(tnumSizeRow(def, it));     // conditional size dropdown
    } else if (def.kind === 'place') {
      box.appendChild(fmtSwatchRow(def, it, PLACE_CARDS, 'otd-mat-grid-2')); // 4 place-card swatches → 2×2
    } else if (def.kind === 'guestbook') {
      box.appendChild(segFmtRow(def, it, 'Size', [{ v: 'a5', label: 'A5 Size' }, { v: 'a4', label: 'A4 Size' }])); // segmented
    } else if (def.kind === 'favour') {
      box.appendChild(fmtSwatchRow(def, it, FAV_CARDS, 'otd-mat-grid-2')); // two tag image swatches
    } else if (def.kind === 'coaster') {
      // single fixed acrylic format — no select needed, just qty + mirror
      box.appendChild(el('p', 'otd-cfg-note', 'Available in premium clear, frosted, or tinted acrylic finishes, offering a luxury keepsake your guests will take home.'));
    } else if (def.kind === 'customdecals') {
      var nrow = el('div', 'otd-cfg-row otd-note-row');
      nrow.appendChild(el('span', 'otd-cfg-label', 'Details'));
      var nwrap = el('div', 'otd-note-wrap');
      var ta = document.createElement('textarea');
      ta.className = 'otd-note-input'; ta.setAttribute('rows', '3');
      ta.setAttribute('data-item-note', def.id);
      ta.setAttribute('placeholder', 'Please specify your dimensions and placement details. Our studio will review your request and prepare a tailored quote during the design phase.');
      ta.value = it.note || '';
      nwrap.appendChild(ta); nrow.appendChild(nwrap); box.appendChild(nrow);
    } else {
      var opts = formatsFor(def).map(function (v) { return { v: v, label: lbl(v) }; });
      box.appendChild(selectRow('Format', 'data-item-fmt', def.id, opts, it.format));
    }
    if (def.kind !== 'vows') { var qr = qtyRow(def); if (qr) box.appendChild(qr); }
    var ur = upsRow(def); if (ur) box.appendChild(ur);
    var rr = ribbonRow(def); if (rr) box.appendChild(rr);
    return box;
  }

  // ── Step III · selected pieces as EXCLUSIVE accordions ────────────────────────
  function selectedDefs() { return ITEMS.filter(function (d) { return state.items[d.id].on; }); }
  function firstInvalidItem() {
    var defs = selectedDefs();
    for (var i = 0; i < defs.length; i++) { if (!itemReady(defs[i])) return defs[i].id; }
    return null;
  }
  // Inline backstop message for one piece's missing required field.
  function itemValidMsg(def) {
    if (itemReady(def)) return '';
    var it = state.items[def.id];
    if (def.kind === 'signage') {
      if (!it.material) return 'Please select a material to continue.';
      if (!it.size) return 'Please select a size to continue.';
    } else if (def.kind === 'coaster') {
      return 'Please add a quantity to continue.';
    } else {
      return 'Please select a ' + (def.kind === 'vows' ? 'style' : 'format') + ' to continue.';
    }
    return '';
  }
  function cfgEstText(def) {
    return itemReady(def) ? ('Estimated ' + fmt0(itemPrice(def))) : 'Set the details below for your estimate';
  }
  // Collapsed-header status badge: "Studio Standard" (title case) → "Customized ✓".
  function statusBadge(def) {
    var it = state.items[def.id];
    if (def.onRequest) return '<span class="otd-acc-badge studio-standard">on request</span>';
    return it.customized
      ? '<span class="otd-acc-badge customized">Customised <span class="otd-acc-tick">✓</span></span>'
      : '<span class="otd-acc-badge studio-standard">Studio Standard</span>';
  }
  function refreshAccHeader(def) {
    var head = root().querySelector('.otd-acc[data-item="' + def.id + '"] .otd-acc-head'); if (!head) return;
    var priceEl = head.querySelector('.otd-acc-price'); if (priceEl) priceEl.textContent = def.onRequest ? '' : (itemReady(def) ? fmt0(itemPrice(def)) : '—');
    var badgeWrap = head.querySelector('.otd-acc-status'); if (badgeWrap) badgeWrap.innerHTML = statusBadge(def);
  }
  // Build one accordion row (collapsed header + lazily-shown config body).
  function accRow(def) {
    var open = state.openItem === def.id;
    var row = el('div', 'otd-acc' + (open ? ' open' : '')); row.dataset.item = def.id;
    var head = el('button', 'otd-acc-head'); head.type = 'button'; head.dataset.accToggle = def.id;
    head.setAttribute('aria-expanded', open ? 'true' : 'false');
    head.innerHTML =
      '<span class="otd-acc-name">' + def.name + '</span>' +
      '<span class="otd-acc-status">' + statusBadge(def) + '</span>' +
      '<span class="otd-acc-price">' + (def.onRequest ? '' : (itemReady(def) ? fmt0(itemPrice(def)) : '—')) + '</span>' +
      '<span class="otd-acc-chev" aria-hidden="true"></span>';
    row.appendChild(head);
    var body = el('div', 'otd-acc-body'); body.hidden = !open;
    if (open) body.appendChild(accBody(def));
    row.appendChild(body);
    return row;
  }
  function accBody(def) {
    var wrap = el('div', 'otd-cfg-panel'); wrap.dataset.item = def.id;
    wrap.appendChild(itemConfig(def));
    var msg = el('p', 'otd-cfg-msg'); msg.dataset.msgFor = def.id; msg.hidden = true; wrap.appendChild(msg);
    if (!def.onRequest) wrap.appendChild(el('p', 'otd-cfg-est otd-cfg-est-br', cfgEstText(def))); // estimate now sits bottom-right
    return wrap;
  }
  function renderCfgPanel() {
    var stage = $('otdCfgStage'); if (!stage) return;
    var defs = selectedDefs();
    stage.innerHTML = '';
    if (!defs.length) {
      stage.innerHTML = '<p class="otd-cfg-empty">Go back and choose at least one piece to configure.</p>';
      return;
    }
    // Default: open the first piece if nothing valid is open.
    if (!state.openItem || defs.map(function (d) { return d.id; }).indexOf(state.openItem) < 0) {
      state.openItem = defs[0].id;
    }
    // Group accordions under thin category headings + divider lines (editorial rhythm).
    var firstGroup = true;
    Object.keys(GROUPS).forEach(function (g) {
      var gd = defs.filter(function (d) { return d.group === g; });
      if (!gd.length) return;
      if (!firstGroup) stage.appendChild(el('hr', 'otd-cat-hr'));
      firstGroup = false;
      stage.appendChild(el('h3', 'otd-cat-head caps', GROUPS[g]));
      gd.forEach(function (def) { stage.appendChild(accRow(def)); });
    });
    refreshAllAccMsgs();
  }
  // Exclusive open/close with smooth scroll to the opened header.
  function openAccordion(id) {
    var stage = $('otdCfgStage'); if (!stage) return;
    state.openItem = (state.openItem === id) ? null : id; // toggle
    // Collapse all, then expand the chosen one (smooth via CSS).
    selectedDefs().forEach(function (def) {
      var row = stage.querySelector('.otd-acc[data-item="' + def.id + '"]'); if (!row) return;
      var isOpen = state.openItem === def.id;
      row.classList.toggle('open', isOpen);
      var head = row.querySelector('.otd-acc-head'); if (head) head.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      var body = row.querySelector('.otd-acc-body'); if (!body) return;
      if (isOpen) { if (!body.firstChild) body.appendChild(accBody(def)); body.hidden = false; }
      else { body.hidden = true; body.innerHTML = ''; }
    });
    refreshAllAccMsgs();
    // Expand in place: the header you clicked stays put and the body grows beneath it.
    // (No scrollIntoView here — with the theme's sticky/smooth scroll it landed erratically.)
  }
  function refreshCfgEst(def) {
    var est = root().querySelector('.otd-acc[data-item="' + def.id + '"] .otd-cfg-est');
    if (est) est.textContent = cfgEstText(def);
    refreshAccHeader(def);
  }
  // Show/clear the inline backstop message inside each open accordion body.
  function refreshAllAccMsgs() {
    selectedDefs().forEach(function (def) {
      var msg = root().querySelector('[data-msg-for="' + def.id + '"]'); if (!msg) return;
      var m = itemValidMsg(def);
      msg.textContent = m; msg.hidden = !m;
    });
  }
  function requestCard(r) {
    var card = el('div', 'otd-card' + (request[r.id] ? ' on' : ''));
    card.innerHTML = '<label class="otd-card-head"><input type="checkbox" data-req-toggle="' + r.id + '"' + (request[r.id] ? ' checked' : '') +
      '><span class="otd-item-name">' + r.name + '</span></label>';
    return card;
  }

  // ── refresh prices on cards + bar ────────────────────────────────────────────
  // Rewrite live upgrade prices + visibility for one item's upgrade list.
  function refreshUps(def) {
    var stack = root().querySelector('[data-ups-for="' + def.id + '"]'); if (!stack) return;
    var it = state.items[def.id];
    def.ups.forEach(function (up) {
      var lab = stack.querySelector('[data-up="' + def.id + '.' + up.k + '"]'); if (!lab) return;
      var price = upPrice(def, it, up);
      var na = price == null;
      lab.classList.toggle('priceup-na', na);
      var pe = lab.querySelector('.upgrade-price');
      if (pe) pe.textContent = na ? '—' : ('+' + fmtUp(price) + (up.suffix != null ? up.suffix : ''));
      if (na && it.ups[up.k]) { // clear ghost upgrade so totals stay honest
        it.ups[up.k] = false;
        var cb = lab.querySelector('input'); if (cb) cb.checked = false;
      }
    });
  }
  // Step II selection cards: just the on-state + a small status on the head.
  function refreshCards() {
    ITEMS.forEach(function (def) {
      var card = root().querySelector('.otd-card[data-item="' + def.id + '"]'); if (!card) return;
      var it = state.items[def.id];
      card.classList.toggle('on', it.on);
      var pr = card.querySelector('.otd-card-price');
      if (pr) pr.textContent = it.on ? (itemReady(def) ? ('est. ' + fmt0(itemPrice(def))) : 'configure in next step →') : '';
    });
  }
  function recalc() {
    var print = printTotal(), dep = designFee();
    var total = print + dep; // running full estimate = print + design fee
    var amt = $('otdBarAmt'), lblEl = $('otdBarLbl'), sub = $('otdBarSub');
    var onReview = (state.step === STEP_REVIEW);
    // Review: show "Due today $dep" beside the Pay-deposit button. Other steps: running total + due-today sub.
    if (lblEl) lblEl.textContent = onReview ? 'Due today' : 'Total So Far';
    if (amt) amt.textContent = onReview ? fmt0(dep) : fmt0(total);
    if (sub) sub.textContent = onReview ? '' : ('Due Today: ' + fmt0(dep));
    if (amt && total !== lastTotal) { amt.classList.remove('pulse'); void amt.offsetWidth; amt.classList.add('pulse'); lastTotal = total; }
    // Hide the running total only on step 1 (it uses the inline Continue); show it on Review (now the due-today).
    var barTot = root() && root().querySelector('.studio-calc-bar .bar-total');
    if (barTot) barTot.style.visibility = (state.step === 1) ? 'hidden' : 'visible';
    var backBtn = $('otdBack'); if (backBtn) backBtn.style.visibility = (state.step === 1) ? 'hidden' : 'visible';
    var bar = root() && root().querySelector('.studio-calc-bar'); if (bar) bar.style.display = (state.step === 1) ? 'none' : ''; // step 1 uses the inline Continue
    refreshCards();
    buildTimeline(); // keep step reachability + completed-ticks current
    if (state.step === STEP_REVIEW) renderReview();
    otdSave(); // persist single-piece config (no-op in whole-suite mode)
    bundleSave(); // persist bundle config (no-op unless bundle mode)
  }
  function selectedCount() { return ITEMS.filter(function (d) { return state.items[d.id].on; }).length + REQUEST.filter(function (r) { return request[r.id]; }).length; }

  // ── review (Step V) — list each piece + price, design line, print note ──────
  function upText(def) {
    var it = state.items[def.id], out = [];
    (def.ups || []).forEach(function (up) { if (it.ups[up.k] && upAllowed(def, it, up)) out.push(up.label.toLowerCase()); });
    if (def.ribbon && it.ribbon && it.ribbon !== 'none') out.push(it.ribbon + ' ribbon');
    return out;
  }
  // Null-safe "spec" (format OR material/size) for review + cart summary.
  // Never emits "null" / undefined — a missing field is simply omitted.
  function specText(def) {
    var it = state.items[def.id], parts = [];
    if (def.kind === 'signage') {
      if (it.material) parts.push(matLabel(it.material));
      if (it.size) parts.push(sizeLabel(it.size));
    } else if (it.format) {
      parts.push(lbl(it.format));
    }
    return parts.join(' · ');
  }
  function renderReview() {
    var r = $('otdRev'); if (!r) return; r.innerHTML = '';
    var any = ITEMS.some(function (d) { return state.items[d.id].on; });
    if (!any) { r.innerHTML = '<div class="rev-l"><span class="rl">Choose your pieces to see your estimate.</span></div>'; return; }
    var print = printTotal(), fee = designFee(), waived = (fee === 0);

    // ── macro totals (no per-item list on load — avoids invoice fatigue) ──
    var tot = el('div', 'otd-sum');
    function sumRow(label, val, cls) {
      var row = el('div', 'otd-sum-row' + (cls ? ' ' + cls : ''));
      row.innerHTML = '<span class="otd-sum-l">' + label + '</span><span class="otd-sum-v">' + val + '</span>';
      return row;
    }
    tot.appendChild(sumRow('Estimated Production Balance', fmt(print)));
    var coverLbl = state.singlePiece ? (((defOf(state.singlePiece) || {}).name || 'This piece') + ' only') : (state.bundleItems ? (state.bundleLabel || 'On-the-day bundle') : 'Full on-the-day stationery');
    tot.appendChild(sumRow('Design &amp; Curation Fee · ' + coverLbl, waived ? 'Pre-paid' : fmt(fee)));
    tot.appendChild(el('hr', 'otd-sum-hr'));
    tot.appendChild(sumRow('Total Suite Investment', fmt(print + fee) + ' <span class="otd-sum-cur">AUD</span>', 'otd-sum-grand'));
    tot.appendChild(sumRow('Due Today', fmt(fee) + ' <span class="otd-sum-cur">AUD</span>', 'otd-sum-due' + (waived ? ' otd-sum-due-zero' : '')));
    var remaining = (print + fee) - fee; // Total Suite Investment − Due Today
    tot.appendChild(sumRow('Remaining Production Balance', fmt(remaining) + ' <span class="otd-sum-cur">AUD</span> <span class="otd-sum-when">(Due upon final print approval)</span>', 'otd-sum-remain'));
    r.appendChild(tot);
    r.appendChild(el('p', 'otd-sum-note', 'Your final production balance is only finalised and processed after you personally approve and sign off on your designs during the proofing phase.'));

    // ── collapsed itemised detail ──
    var accBtn = el('button', 'otd-sum-toggle'); accBtn.type = 'button';
    accBtn.innerHTML = '<span class="otd-sum-chev" aria-hidden="true">›</span> Review Itemised Selection Detail';
    var detail = el('div', 'otd-sum-detail'); detail.hidden = true;
    var dRow = el('div', 'rev-l');
    dRow.innerHTML = '<span class="rl">Design · ' + (state.prepaid ? 'Pre-Paid' + (state.designCode ? ' (code ' + state.designCode + ')' : '') : (state.tier === 'indiv' ? 'Tailored Touch' : 'Studio Collection')) + '</span><span class="rp">' + (waived ? '–' : fmt(fee)) + '</span>';
    detail.appendChild(dRow);
    ITEMS.forEach(function (def) {
      var it = state.items[def.id]; if (!it.on) return;
      if (def.onRequest) {
        var dd = el('div', 'rev-l');
        dd.innerHTML = '<span class="rl">' + def.name + (it.note ? ' · ' + it.note : '') + ' <em style="color:#6b6966">(we’ll quote separately)</em></span><span class="rp"></span>';
        detail.appendChild(dd); return;
      }
      var spec = specText(def), ups = upText(def);
      var desc = def.name + (spec ? ' · ' + spec : '') + (ups.length ? ' (' + ups.join(', ') + ')' : '') + ' × ' + itemQty(def, it);
      var d = el('div', 'rev-l');
      d.innerHTML = '<span class="rl">' + desc + ' <button class="ed" data-goto="4">edit</button></span><span class="rp">' + fmt(itemPrice(def)) + '</span>';
      detail.appendChild(d);
    });
    var pn = el('div', 'rev-l');
    pn.innerHTML = '<span class="rl" style="color:#6b6966;font-style:italic">Total estimated production</span><span class="rp" style="color:#6b6966">' + fmt(print) + '</span>';
    detail.appendChild(pn);
    accBtn.addEventListener('click', function () { detail.hidden = !detail.hidden; accBtn.classList.toggle('open', !detail.hidden); });
    r.appendChild(accBtn); r.appendChild(detail);

    // ── Design Journey (3-step vertical timeline; Step 1 conditional) ──
    var journey = el('div', 'otd-journey');
    journey.appendChild(el('p', 'otd-journey-h caps', 'Your design journey'));
    var s1 = waived
      ? ['Activate Your Production Slot', 'Your exclusive code has successfully applied your pre-paid status. Your production slot is officially confirmed, and our studio will seamlessly initiate your design phase.']
      : ['Secure Your Date', 'Your initial Design Fee secures your production slot in our studio calendar and officially initiates your dedicated design phase.'];
    var steps = [
      s1,
      ['Digital Proofing & Refinement', 'Our studio will work closely with you to perfect your typography, layout, and stationery details. You can adjust your final pieces and quantities at any time during this phase.'],
      ['Final Approval & Production', 'Nothing goes to print without your final digital approval. Your remaining production balance is only processed after you completely sign off on the designs.']
    ];
    steps.forEach(function (s, i) {
      var st = el('div', 'otd-journey-step');
      st.innerHTML = '<span class="otd-journey-num">' + (i + 1) + '</span>' +
        '<span class="otd-journey-txt"><span class="otd-journey-t">Step ' + (i + 1) + ': ' + s[0] + '</span><span class="otd-journey-b">' + s[1] + '</span></span>';
      journey.appendChild(st);
    });
    r.appendChild(journey);

    r.querySelectorAll('button.ed[data-goto]').forEach(function (b) { b.addEventListener('click', function () { goTo(+b.dataset.goto); }); });
  }

  // ── top interactive step timeline ────────────────────────────────────────────
  // Plain Roman numerals I / II / III / IV / V separated by thin " / " slashes —
  // no circles, no connector lines. Clickable with the same reachability gating.
  var TIMELINE = ['Guest Details', 'Design', 'Choose Pieces', 'Configure', 'Summary'];
  var TL_ROMAN = ['I', 'II', 'III', 'IV', 'V'];
  function buildTimeline() {
    var tl = $('otdTimeline'); if (!tl) return;
    tl.innerHTML = '';
    var nums = el('div', 'otd-tl-nums');
    var shown = state.singlePiece ? [1, STEP_CONFIGURE, STEP_REVIEW] : (state.bundleItems ? [1, STEP_SELECT, STEP_CONFIGURE, STEP_REVIEW] : [1, 2, 3, 4, 5]); // single skips Design+Choose; bundle skips Design
    shown.forEach(function (n, idx) {
      var label = TIMELINE[n - 1];
      var done = state.step > n;          // a step before the current one = completed
      var active = state.step === n;
      var clickable = stepReachable(n) && !active; // current + any satisfied step
      var stepEl = el('button', 'otd-tl-num' + (active ? ' active' : '') + (done ? ' done' : '') + (clickable ? '' : ' muted'), TL_ROMAN[idx]);
      stepEl.type = 'button';
      if (clickable) stepEl.dataset.tlJump = String(n); else stepEl.disabled = true;
      stepEl.setAttribute('aria-current', active ? 'step' : 'false');
      stepEl.setAttribute('aria-label', 'Step ' + (idx + 1) + ': ' + label);
      nums.appendChild(stepEl);
      if (idx < shown.length - 1) nums.appendChild(el('span', 'otd-tl-slash', '/')); // thin muted slash
    });
    tl.appendChild(nums);
    // Show only the ACTIVE step's label (small caps) — keeps it clean.
    tl.appendChild(el('p', 'otd-tl-active-label', TIMELINE[state.step - 1] || ''));
  }

  // ── navigation ───────────────────────────────────────────────────────────────
  // Step III is valid only when EVERY selected piece is fully configured.
  function configAllValid() { return firstInvalidItem() == null; }
  function paneValid(n) {
    if (n === 1) return state.guests > 0;
    if (n === STEP_DESIGN) return state.prepaid ? state.codeOK : true; // pre-paid needs a valid code
    if (n === STEP_SELECT) return selectedCount() > 0;
    if (n === STEP_CONFIGURE) return configAllValid();
    return true; // V always valid
  }
  // Timeline gating: a step is reachable when its prerequisites are met.
  function stepReachable(n) {
    if (n === 1) return true;
    if (n === STEP_DESIGN) return state.guests > 0;
    if (n === STEP_SELECT) return state.guests > 0;
    return state.guests > 0 && selectedCount() > 0; // configure / review
  }
  function validate() {
    var last = state.step === STEP_REVIEW, nx = $('otdNext'); if (!nx) return;
    nx.disabled = !paneValid(state.step);
    nx.textContent = last ? (designFee() === 0 ? 'CONFIRM & START DESIGN' : 'PAY DESIGN FEE & START DESIGN') : 'Continue';
    var s1 = root() && root().querySelector('.otd-step1-next'); if (s1) s1.classList.toggle('is-ready', paneValid(1));
    if (state.step === STEP_CONFIGURE) refreshAllAccMsgs();
  }
  function renderStep() {
    document.querySelectorAll('.otd-master .scalc-pane').forEach(function (p) { p.classList.toggle('active', +p.dataset.step === state.step); });
    if (state.step === STEP_CONFIGURE) { renderCfgPanel(); syncQtyAutofill(); }
    buildTimeline();
    var bk = $('otdBack'); if (bk) bk.disabled = state.step === 1;
    if (!firstRender) requestAnimationFrame(function () {
      // On every tab switch, return to the START of the calculator (its timeline + the new
      // step's heading) so the new tab is reliably in view — fixes the last tab landing
      // off-screen when stepping from a tall step into a short one.
      var target = root() || document.querySelector('.otd-master .scalc-pane.active');
      if (!target) return;
      try {
        // Fixed offset (not the sticky header's live offsetHeight, which reads inconsistently
        // mid-transition and made the landing position jump around).
        target.style.scrollMarginTop = '90px';
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (x) { }
    });
    firstRender = false; validate(); recalc();
  }
  function go(dir) {
    // Single-piece mode: guests (1) → configure (4) → review (5); tabs 2 & 3 are skipped.
    if (state.singlePiece) {
      if (dir > 0 && state.step === 1) { if (!paneValid(1)) return; state.step = STEP_CONFIGURE; renderStep(); return; }
      if (dir < 0 && state.step === STEP_CONFIGURE) { state.step = 1; renderStep(); return; }
    }
    // Bundle mode: guests (1) → SELECT the bundle pieces (3) → configure (4) → review (5); the Design tab (2) is skipped.
    if (state.bundleItems) {
      if (dir > 0 && state.step === 1) { if (!paneValid(1)) return; state.step = STEP_SELECT; renderStep(); return; }
      if (dir < 0 && state.step === STEP_SELECT) { state.step = 1; renderStep(); return; }
    }
    if (state.step === STEP_CONFIGURE) {
      if (dir > 0) {
        // Backstop: every piece must be valid before leaving Configure.
        var bad = firstInvalidItem();
        if (bad) { if (state.openItem !== bad) openAccordion(bad); refreshAllAccMsgs(); validate(); return; }
        state.step = STEP_REVIEW; renderStep(); return;
      }
      state.step = STEP_SELECT; renderStep(); return; // Back → selection (no validation on Back)
    }
    if (dir > 0 && state.step === STEP_REVIEW) { submit(); return; }
    if (dir > 0 && !paneValid(state.step)) return;
    state.step = Math.min(STEPS.length, Math.max(1, state.step + dir)); renderStep();
  }
  function goTo(n) { state.step = n; renderStep(); }
  // Free jump from the timeline — gated by reachability + Configure validity.
  function jumpTo(n) {
    if (n === state.step) return;
    if (!stepReachable(n)) return;
    // Leaving Configure forward requires all pieces valid; auto-open first invalid.
    if (state.step === STEP_CONFIGURE && n > STEP_CONFIGURE) {
      var bad = firstInvalidItem();
      if (bad) { if (state.openItem !== bad) openAccordion(bad); refreshAllAccMsgs(); validate(); return; }
    }
    state.step = n; renderStep();
  }

  // ── interactions ─────────────────────────────────────────────────────────────
  // Push guest-driven quantities into the active config panel (auto items only).
  function syncQtyAutofill() {
    ITEMS.forEach(function (def) {
      var it = state.items[def.id]; if (it.qtyManual) return;
      var inp = root().querySelector('[data-item-qty="' + def.id + '"]');
      if (inp) inp.value = itemQty(def, it) || '';
      var panel = root().querySelector('.otd-cfg-panel[data-item="' + def.id + '"]');
      var auto = panel && panel.querySelector('.otd-qty-auto');
      if (auto && def.qmode === 'table') auto.textContent = 'one per table (' + tables() + ')';
      if (auto && def.id === 'service') auto.textContent = (it.format === 'fan') ? 'one per guest' : 'about 1 per couple (60%)';
    });
  }
  function setGuests(v) { state.guests = Math.max(0, Math.min(3000, parseInt(v, 10) || 0)); syncQtyAutofill(); recalc(); validate(); }
  function toggleItem(id, on) {
    var it = state.items[id]; it.on = !!on;
    if (on) { applyDefaults(defOf(id)); it.customized = false; } // smart defaults = "Standard finishes"
    // keep an open accordion valid as selection changes
    if (state.openItem && !state.items[state.openItem].on) state.openItem = null;
    recalc(); validate();
  }
  // Any user override flips the piece's status badge to "Customized ✓".
  function markCustomized(id) { if (state.items[id]) state.items[id].customized = true; }
  function afterCfgChange(def) { if (def) { refreshUps(def); refreshCfgEst(def); } syncQtyAutofill(); recalc(); validate(); }
  function setItemFmt(id, v) {
    state.items[id].format = v; markCustomized(id);
    // reflect image-swatch selection if this item uses format tiles (e.g. service)
    var panel = root().querySelector('.otd-cfg-panel[data-item="' + id + '"]');
    var grid = panel && panel.querySelector('[data-fmt-grid="' + id + '"]');
    if (grid) grid.querySelectorAll('[data-item-fmtsw]').forEach(function (b) {
      var on = b.dataset.itemFmtsw === (id + '.' + v);
      b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    afterCfgChange(defOf(id));
  }
  // Menus Tier 1: switch Cardstock/Fabric → reset format to that material's default + re-render Tier 2.
  function setMenuMat(id, mat) {
    var it = state.items[id], def = defOf(id);
    if (menuMatOf(it) !== mat) it.format = (mat === 'fabric') ? 'fabric-sm' : 'flat-sm';
    markCustomized(id);
    var panel = root().querySelector('.otd-cfg-panel[data-item="' + id + '"]');
    if (panel) {
      var seg = panel.querySelector('[data-menu-mat-seg="' + id + '"]');
      if (seg) seg.querySelectorAll('[data-menu-mat]').forEach(function (b) { b.classList.toggle('on', b.dataset.menuMat === (id + '.' + mat)); });
      var oldRow = panel.querySelector('[data-menu-fmt-row="' + id + '"]');
      if (oldRow && def) { var fresh = menuFmtRow(def, it); oldRow.parentNode.replaceChild(fresh, oldRow); }
    }
    afterCfgChange(def);
  }
  // Table numbers: switch material → reset format to that material's first size + re-render Size dropdown.
  function setTnumMat(id, mat) {
    var it = state.items[id], def = defOf(id);
    if (tnumMatOf(it) !== mat) it.format = (TNUM_SIZES[mat][0] || {}).v || it.format;
    markCustomized(id);
    var panel = root().querySelector('.otd-cfg-panel[data-item="' + id + '"]');
    if (panel) {
      var grid = panel.querySelector('[data-tnum-grid="' + id + '"]');
      if (grid) grid.querySelectorAll('[data-tnum-mat]').forEach(function (b) {
        var on = b.dataset.tnumMat === (id + '.' + mat);
        b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var oldRow = panel.querySelector('[data-tnum-size-row="' + id + '"]');
      if (oldRow && def) { var fresh = tnumSizeRow(def, it); oldRow.parentNode.replaceChild(fresh, oldRow); }
    }
    afterCfgChange(def);
  }
  function setItemMat(id, v) {
    var it = state.items[id], def = defOf(id), prevSize = it.size;
    it.material = v;
    // Preserve the chosen size (and upgrades, untouched) when it stays valid for the new
    // material; otherwise fall back to a sensible default so it's never left blank.
    var avail = sizesFor(id, v);
    if (!prevSize || avail.indexOf(prevSize) < 0) {
      var d = DEFAULTS[id], wanted = (d && d.size) ? d.size : null;
      it.size = (wanted && avail.indexOf(wanted) >= 0) ? wanted : (avail[0] || null);
    }
    markCustomized(id);
    var panel = root().querySelector('.otd-cfg-panel[data-item="' + id + '"]'); if (!panel) { afterCfgChange(def); return; }
    // Reflect the selected material tile (image-swatch picker)
    var grid = panel.querySelector('[data-mat-grid="' + id + '"]');
    if (grid) grid.querySelectorAll('[data-item-matsw]').forEach(function (b) {
      var on = b.dataset.itemMatsw === (id + '.' + v);
      b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    var sr = panel.querySelector('[data-size-row="' + id + '"]');
    if (sr && def) { var fresh = sizeRow(def, it); sr.parentNode.replaceChild(fresh, sr); }
    afterCfgChange(def);
  }
  function setItemSize(id, v) { state.items[id].size = v || null; markCustomized(id); afterCfgChange(defOf(id)); }
  // Church booklets: pick a format card; "Church Booklet" keeps/defaults a cover variant + reveals the sub-row.
  function setServiceCard(id, card) {
    var it = state.items[id], def = defOf(id);
    if (card === 'booklet') { if (it.format !== 'cover-only' && it.format !== 'cover-matte') it.format = 'cover-only'; }
    else it.format = card;
    markCustomized(id);
    var panel = root().querySelector('.otd-cfg-panel[data-item="' + id + '"]');
    if (panel) {
      var curCard = serviceCardOf(it);
      var grid = panel.querySelector('[data-service-grid="' + id + '"]');
      if (grid) grid.querySelectorAll('[data-service-card]').forEach(function (b) {
        var on = b.dataset.serviceCard === (id + '.' + curCard);
        b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var oldRow = panel.querySelector('[data-service-cover-row="' + id + '"]');
      if (oldRow && def) { var fresh = serviceCoverRow(def, it); oldRow.parentNode.replaceChild(fresh, oldRow); }
    }
    afterCfgChange(def);
  }
  function setServiceCover(id, fmt) {
    var it = state.items[id], def = defOf(id); it.format = fmt; markCustomized(id);
    var panel = root().querySelector('.otd-cfg-panel[data-item="' + id + '"]');
    if (panel) { var seg = panel.querySelector('[data-service-cover-seg="' + id + '"]'); if (seg) seg.querySelectorAll('[data-service-cover]').forEach(function (b) { b.classList.toggle('on', b.dataset.serviceCover === (id + '.' + fmt)); }); }
    afterCfgChange(def);
  }
  // Vow books: material/size compose the FEL key (<size>-<material>).
  function setVowMat(id, m) {
    var it = state.items[id]; it.format = vowSizeOf(it) + '-' + m; markCustomized(id);
    var panel = root().querySelector('.otd-cfg-panel[data-item="' + id + '"]');
    var grid = panel && panel.querySelector('[data-vow-grid="' + id + '"]');
    if (grid) grid.querySelectorAll('[data-vow-matsw]').forEach(function (b) {
      var on = b.dataset.vowMatsw === (id + '.' + m);
      b.classList.toggle('on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    var oldRow = panel && panel.querySelector('[data-vow-size-row="' + id + '"]');
    if (oldRow) { var fresh = vowSizeRow(defOf(id), it); oldRow.parentNode.replaceChild(fresh, oldRow); }
    afterCfgChange(defOf(id));
  }
  function setVowSize(id, s) { var it = state.items[id]; it.format = s + '-' + vowMatOf(it); markCustomized(id); afterCfgChange(defOf(id)); }
  function setItemQty(id, v) { var it = state.items[id]; it.qty = Math.max(0, parseInt(v, 10) || 0); it.qtyManual = true; markCustomized(id); afterCfgChange(defOf(id)); }
  function toggleItemUp(idKey, on) {
    var parts = idKey.split('.'), id = parts[0], k = parts[1];
    if (!state.items[id].ups) state.items[id].ups = {};
    state.items[id].ups[k] = !!on; markCustomized(id); afterCfgChange(defOf(id));
  }
  function setItemRibbon(idKey) {
    var parts = idKey.split('.'), id = parts[0], type = parts[1];
    state.items[id].ribbon = type; markCustomized(id);
    var seg = root().querySelector('[data-ribbon-for="' + id + '"]');
    if (seg) seg.querySelectorAll('[data-item-ribbon]').forEach(function (b) { b.classList.toggle('on', b.dataset.itemRibbon === idKey); });
    afterCfgChange(defOf(id));
  }
  // Selecting a design tier exits pre-paid mode (un-greys, hides + clears the code).
  function setTier(t) {
    state.tier = t; state.prepaid = false; state.codeOK = false; state.designCode = '';
    document.querySelectorAll('.otd-master [data-tier]').forEach(function (b) {
      b.classList.toggle('on', b.dataset.tier === t); b.classList.remove('otd-dim');
      b.setAttribute('aria-pressed', b.dataset.tier === t ? 'true' : 'false');
    });
    var card = $('otdCodeCard'); if (card) { card.classList.remove('on'); card.setAttribute('aria-pressed', 'false'); }
    var box = $('otdCodeBox'); if (box) box.hidden = true;
    var ci = $('otdCodeInput'); if (ci) ci.value = ''; var m = $('otdCodeMsg'); if (m) { m.hidden = true; m.textContent = ''; }
    recalc(); validate();
  }
  // Pre-Paid Design card: one of three peer options (all stay visible + clickable).
  // Selecting it reveals the code field; selecting a tier exits pre-paid.
  function setPrepaid(on) {
    state.prepaid = !!on;
    var card = $('otdCodeCard'), box = $('otdCodeBox');
    if (card) { card.classList.toggle('on', on); card.setAttribute('aria-pressed', on ? 'true' : 'false'); }
    if (box) box.hidden = !on;
    // tiers stay selectable — just clear their highlight while pre-paid is the active choice
    document.querySelectorAll('.otd-master [data-tier]').forEach(function (b) { b.classList.remove('otd-dim'); if (on) { b.classList.remove('on'); b.setAttribute('aria-pressed', 'false'); } });
    if (on) { var inp = $('otdCodeInput'); if (inp) requestAnimationFrame(function () { try { inp.focus(); } catch (e) { } }); }
    else {
      state.codeOK = false; state.designCode = ''; var ci = $('otdCodeInput'); if (ci) ci.value = ''; var m = $('otdCodeMsg'); if (m) { m.hidden = true; m.textContent = ''; }
      document.querySelectorAll('.otd-master [data-tier]').forEach(function (b) { var sel = b.dataset.tier === state.tier; b.classList.toggle('on', sel); b.setAttribute('aria-pressed', sel ? 'true' : 'false'); });
    }
    recalc(); validate();
  }
  // A recognised code (within pre-paid mode) confirms the fee waiver.
  function applyCode(val) {
    var code = (val || '').trim();
    state.designCode = code;
    var msg = $('otdCodeMsg');
    if (!code) { state.codeOK = false; if (msg) { msg.hidden = true; msg.textContent = ''; } recalc(); validate(); return; }
    state.codeOK = cfg.codes.indexOf(code.toLowerCase()) >= 0;
    if (msg) {
      msg.hidden = false;
      msg.className = 'otd-code-msg ' + (state.codeOK ? 'ok' : 'bad');
      msg.textContent = state.codeOK ? '✓ Code applied. Your design fee has been adjusted.' : 'This code is not recognised. Please verify and try again.';
    }
    recalc(); validate();
  }
  function toggleReq(id, on) { request[id] = !!on; var c = root().querySelector('[data-req-toggle="' + id + '"]'); if (c) { c.checked = !!on; var card = c.closest('.otd-card'); if (card) card.classList.toggle('on', !!on); } recalc(); validate(); }

  // ── cart ─────────────────────────────────────────────────────────────────────
  function gidToNumeric(g) { if (!g) return null; var m = String(g).match(/(\d+)$/); return m ? m[1] : null; }
  function openCart() {
    // Open the theme's slide-out drawer (cart_type: drawer) — refresh its contents + show it.
    try {
      if (document.querySelector && document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        return;
      }
    } catch (e) { }
    window.location.href = '/cart';
  }
  function itemsSummary() {
    // Full itemised tentative quote (line + spec + upgrades + qty + estimated line price) — visible to client + studio.
    return ITEMS.filter(function (d) { return state.items[d.id].on && !d.onRequest; }).map(function (def) {
      var spec = specText(def);
      var ups = upText(def);
      return def.name + (spec ? ' (' + spec + ')' : '') + (ups.length ? ' [' + ups.join(', ') + ']' : '') + ' ×' + itemQty(def, state.items[def.id]) + ' · ' + fmt(itemPrice(def));
    }).join(' | ');
  }
  // Single OTD piece (from a collection product page) → reconciled $100 design (capped at $200) via FelDesign.
  async function submitSinglePiece() {
    var nx = $('otdNext'); var id = state.singlePiece, def = defOf(id);
    if (!def) { showError('Piece not found.'); return; }
    if (!window.FelDesign) { showError('Sorry — the order helper did not load. Please refresh and try again.'); return; }
    var orig = nx ? nx.textContent : ''; if (nx) { nx.disabled = true; nx.textContent = 'Adding…'; }
    var it = state.items[id], spec = specText(def), ups = upText(def), print = printTotal();
    var printProps = {
      'Product': def.name,
      '_edit_url': (typeof location !== 'undefined' ? location.pathname + '?piece=' + id : ''),
      'Guests': String(state.guests),
      'Specification': def.name + (spec ? ' · ' + spec : '') + (ups.length ? ' (' + ups.join(', ') + ')' : '') + ' × ' + itemQty(def, it),
      'Estimated production balance (AUD)': fmt(print)
    };
    if (state.date) printProps['Production secured for'] = prettyDate(state.date);
    try {
      await window.FelDesign.checkout({
        otdPiece: true, pieceId: id, printKey: 'otd-' + id,
        printProps: printProps, designProps: { 'Booked from': def.name + ' (on-the-day piece)' }, redirect: true
      });
    } catch (err) {
      showError('Sorry, something went wrong: ' + (err.message || 'unknown') + ' — email info@felicitations.com.au');
      if (nx) { nx.disabled = false; nx.textContent = orig; }
    }
  }
  async function submitBundle() {
    var nx = $('otdNext');
    if (!window.FelDesign) { showError('Sorry — the order helper did not load. Please refresh and try again.'); return; }
    var chosen = state.bundleItems.filter(function (id) { return state.items[id] && state.items[id].on; });
    if (!chosen.length) { showError('Select at least one piece for your bundle.'); return; }
    var orig = nx ? nx.textContent : ''; if (nx) { nx.disabled = true; nx.textContent = 'Adding…'; }
    // ONE Print Configuration line for the whole bundle (not a separate line per piece) — each
    // chosen piece is listed as its own row under that single line.
    var printProps = { 'Bundle': state.bundleLabel, '_edit_url': (typeof location !== 'undefined' ? location.pathname : '') };
    if (state.guests > 0) printProps['Guests'] = String(state.guests);
    chosen.forEach(function (id) {
      var def = defOf(id), it = state.items[id], spec = specText(def), ups = upText(def);
      printProps[def.name] = (spec || def.name) + (ups.length ? ' (' + ups.join(', ') + ')' : '') + ' × ' + itemQty(def, it);
    });
    // Total print estimate for the bundle (sum of the chosen pieces) — shown on every cart print line.
    printProps['Estimated production balance (AUD)'] = fmt(chosen.reduce(function (s, id) { return s + itemPrice(defOf(id)); }, 0));
    if (state.date) printProps['Production secured for'] = prettyDate(state.date);
    var printItems = [{ printKey: 'otd-b-' + state.bundleKey, printProps: printProps }];
    try {
      await window.FelDesign.checkout({
        otdBundle: true, bundleKey: state.bundleKey, printItems: printItems,
        designProps: { 'Booked from': state.bundleLabel + ' (on-the-day bundle)', 'Pieces': chosen.map(function (id) { return (defOf(id) || {}).name; }).join(', ') },
        redirect: true
      });
    } catch (err) {
      showError('Sorry, something went wrong: ' + (err.message || 'unknown') + ' — email info@felicitations.com.au');
      if (nx) { nx.disabled = false; nx.textContent = orig; }
    }
  }
  async function submit() {
    if (state.singlePiece) return submitSinglePiece();
    if (state.bundleItems) return submitBundle();
    var nx = $('otdNext');
    if (state.prepaid && !state.codeOK) { showError('Enter a valid design code, or choose Studio Collection / Tailored Touch.'); return; }
    var depGid = state.tier === 'indiv' ? cfg.depIndivGid : cfg.depSuiteGid;
    var depId = gidToNumeric(depGid);
    if (!depId) { showError('Design Fee variant not configured (section settings).'); return; }
    var orig = nx.textContent; nx.disabled = true; nx.textContent = 'Adding…';
    var print = printTotal(), fee = designFee();
    var reqList = ITEMS.filter(function (d) { return d.onRequest && state.items[d.id].on; }).map(function (d) { var n = (state.items[d.id].note || '').trim(); return d.name + (n ? ' (' + n + ')' : ''); }).join(', ');
    // Clean, ordered cart line: design → guests/date → investment; long pieces list collapsed by otd-cart-tidy.js.
    var props = {
      'Design': state.prepaid ? 'Existing client' : ('Full on-the-day suite · ' + (state.tier === 'indiv' ? 'Tailored Touch' : 'Studio Collection'))
    };
    props['Guests'] = String(state.guests);
    if (state.date) props['Production secured for'] = prettyDate(state.date);
    props['Estimated production balance (AUD)'] = fmt(print); // unified label across every print-defining cart line
    props['Pieces'] = itemsSummary();
    props['On request (to quote)'] = reqList;
    props['_scope_pieces'] = 'OTD'; props['_scope_source'] = 'OTD-master'; props['_scope_collection'] = state.tier === 'indiv' ? 'TT' : 'SC';
    props['_scope_design_fee'] = String(fee); props['_Print estimate (AUD)'] = fmt(print);
    if (state.codeOK) { props['Design code'] = state.designCode; props['_design_fee_waived'] = 'true'; }
    try { props['_otd_state'] = JSON.stringify(stateSnapshot()); } catch (e) { } // for "Edit your selection" restore
    Object.keys(props).forEach(function (k) { if (!props[k]) delete props[k]; });
    try {
      // A recognised design code waives the fee — apply the matching Shopify discount so checkout is $0.
      if (state.codeOK && state.designCode) { try { await fetch('/discount/' + encodeURIComponent(state.designCode), { credentials: 'same-origin' }); } catch (e) { } }
      try {
        var cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
        var dropIds = [Number(gidToNumeric(cfg.depSuiteGid)), Number(gidToNumeric(cfg.depIndivGid))];
        var updates = {}; (cart.items || []).forEach(function (li) { if (dropIds.indexOf(li.variant_id) >= 0) updates[li.key] = 0; });
        if (Object.keys(updates).length) await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ updates }) });
      } catch (e) { }
      var res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ items: [{ id: depId, quantity: 1, properties: props }] }) });
      if (!res.ok) { var b = await res.text().catch(function () { return ''; }); throw new Error('cart/add.js ' + res.status + ': ' + b.slice(0, 160)); }
      openCart(); nx.disabled = false; nx.textContent = orig;
    } catch (err) { console.error('[OTDMaster.submit]', err); showError('Something went wrong: ' + (err.message || 'unknown')); nx.disabled = false; nx.textContent = orig; }
  }
  async function sendQuote_unused() {
    var nx = $('otdNext'); var email = ($('otdEmail') && $('otdEmail').value || '').trim();
    if (!state.orderRef) { showError('Enter your previous order number.'); return; }
    if (!email) { showError('Add an email so we can send your quote.'); return; }
    var orig = nx.textContent; nx.disabled = true; nx.textContent = 'Sending…';
    var lines = ['OTD print quote (returning — design paid)', 'Pieces: ' + itemsSummary(), 'Guests: ' + state.guests, 'Previous order #: ' + state.orderRef, 'Print estimate: ' + fmt(printTotal())];
    var pp = new URLSearchParams(); pp.set('form_type', 'contact'); pp.set('utf8', '✓');
    pp.set('contact[name]', ($('otdName') && $('otdName').value || '').trim() || ('Order ' + state.orderRef));
    pp.set('contact[email]', email); pp.set('contact[Order reference]', state.orderRef); pp.set('contact[body]', lines.join('\n'));
    try {
      var res = await fetch('/contact', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, credentials: 'same-origin', body: pp.toString() });
      if (!res.ok && res.status !== 0) throw new Error('contact ' + res.status);
      var rev = $('otdRev'); if (rev) rev.innerHTML = '<div class="rev-l"><span class="rl">Thank you — we’ll email your print quote shortly.</span></div>'; nx.textContent = 'Sent ✓';
    } catch (e) { showError('Could not send — email info@felicitations.com.au.'); nx.disabled = false; nx.textContent = orig; }
  }
  function showError(m) { var e = $('otd-err'); if (!e) { e = el('p', 'err'); e.id = 'otd-err'; var rev = $('otdRev'); if (rev && rev.parentNode) rev.parentNode.appendChild(e); } e.textContent = m; }

  // ── events ───────────────────────────────────────────────────────────────────
  function wire(rootEl) {
    rootEl.addEventListener('click', function (e) {
      if (e.target.closest('a')) return; // let brand links (View Our Process / Collections) navigate
      var t = e.target.closest('[data-action],[data-tier],[data-prepaid],[data-item-ribbon],[data-item-matsw],[data-vow-matsw],[data-item-fmtsw],[data-menu-mat],[data-tnum-mat],[data-service-card],[data-service-cover],[data-tl-jump],[data-acc-toggle]');
      if (!t) return; var ds = t.dataset;
      if (ds.action === 'go') { go(parseInt(ds.dir, 10)); return; }
      if (ds.action === 'infoOpen') { var im = $('otdInfo'); if (im) { im.hidden = false; try { document.documentElement.style.overflow = 'hidden'; } catch (e) { } } return; }
      if (ds.action === 'infoClose') { var ic = $('otdInfo'); if (ic) { ic.hidden = true; try { document.documentElement.style.overflow = ''; } catch (e) { } } return; }
      if (ds.tier) { setTier(ds.tier); return; }
      if (ds.prepaid != null) { if (!e.target.closest('#otdCodeBox')) setPrepaid(!state.prepaid); return; }
      if (ds.itemRibbon) { setItemRibbon(ds.itemRibbon); return; }
      if (ds.itemMatsw != null) { var mp = ds.itemMatsw.split('.'); setItemMat(mp[0], mp.slice(1).join('.')); return; }
      if (ds.vowMatsw != null) { var vp = ds.vowMatsw.split('.'); setVowMat(vp[0], vp[1]); return; }
      if (ds.itemFmtsw != null) { var fp = ds.itemFmtsw.split('.'); setItemFmt(fp[0], fp.slice(1).join('.')); return; }
      if (ds.menuMat != null) { var nmp = ds.menuMat.split('.'); setMenuMat(nmp[0], nmp[1]); return; }
      if (ds.tnumMat != null) { var tmp = ds.tnumMat.split('.'); setTnumMat(tmp[0], tmp[1]); return; }
      if (ds.serviceCard != null) { var sp = ds.serviceCard.split('.'); setServiceCard(sp[0], sp[1]); return; }
      if (ds.serviceCover != null) { var cp = ds.serviceCover.split('.'); setServiceCover(cp[0], cp.slice(1).join('.')); return; }
      if (ds.tlJump != null) { jumpTo(parseInt(ds.tlJump, 10)); return; }
      if (ds.accToggle != null) { openAccordion(ds.accToggle); return; }
    });
    rootEl.addEventListener('change', function (e) {
      var t = e.target;
      if (t.dataset.itemToggle) { toggleItem(t.dataset.itemToggle, t.checked); return; }
      if (t.dataset.reqToggle) { toggleReq(t.dataset.reqToggle, t.checked); return; }
      if (t.dataset.itemUp != null) { toggleItemUp(t.dataset.itemUp, t.checked); return; }
      if (t.dataset.itemFmt != null) { setItemFmt(t.dataset.itemFmt, t.value); return; }
      if (t.dataset.itemMat != null) { setItemMat(t.dataset.itemMat, t.value); return; }
      if (t.dataset.itemSize != null) { setItemSize(t.dataset.itemSize, t.value); return; }
      if (t.dataset.vowSize != null) { setVowSize(t.dataset.vowSize, t.value); return; }
      if (t.dataset.tnumSize != null) { setItemFmt(t.dataset.tnumSize, t.value); return; }
      if (t.dataset.menuFmt != null) { setItemFmt(t.dataset.menuFmt, t.value); return; }
    });
    rootEl.addEventListener('input', function (e) {
      var t = e.target;
      if (t.id === 'otdGuests') setGuests(t.value);
      if (t.id === 'otdDate') state.date = t.value;
      if (t.id === 'otdCodeInput') applyCode(t.value);
      if (t.dataset.itemQty != null) setItemQty(t.dataset.itemQty, t.value);
      if (t.dataset.itemNote != null && state.items[t.dataset.itemNote]) state.items[t.dataset.itemNote].note = t.value;
    });
    // Keyboard activation for the role="button" tier cards (Enter / Space).
    rootEl.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      var tc = e.target.closest && e.target.closest('[data-tier]');
      if (tc && !e.target.closest('a')) { e.preventDefault(); setTier(tc.dataset.tier); return; }
      var pc = e.target.closest && e.target.closest('[data-prepaid]');
      if (pc) { e.preventDefault(); setPrepaid(!state.prepaid); }
    });
  }

  function readCfg(e) {
    var d = e.dataset;
    cfg.depSuiteGid = d.depositSuiteId || ''; cfg.depIndivGid = d.depositIndivId || '';
    cfg.feeSuite = parseFloat(d.feeSuite) || 200; cfg.feeIndiv = parseFloat(d.feeIndiv) || 300;
    cfg.cartBehavior = d.cartBehavior || 'cart'; cfg.drawerSelector = d.drawerSelector || '';
    cfg.codes = (d.designCodes || '').split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
  }
  // Pull any browser-restored input values into state (fixes "can't continue" after navigating
  // back to the calculator, when the guests field keeps its value but state had reset to 0).
  function syncFromInputs() {
    var g = $('otdGuests'); if (g && g.value !== '' && g.value != null) setGuests(g.value);
    var d = $('otdDate'); if (d && d.value) state.date = d.value;
    validate();
  }
  // ── "Edit your selection" — snapshot state into the cart line, restore it on return ──
  function stateSnapshot() {
    var items = {};
    ITEMS.forEach(function (d) {
      var it = state.items[d.id]; if (!it.on) return;
      items[d.id] = { format: it.format, material: it.material, size: it.size, qty: it.qty, qtyManual: it.qtyManual ? 1 : 0, ribbon: it.ribbon, ups: it.ups || {}, note: it.note || '', customized: it.customized ? 1 : 0 };
    });
    return { v: 1, guests: state.guests, date: state.date, tier: state.tier, prepaid: state.prepaid ? 1 : 0, code: state.designCode || '', items: items };
  }
  function rehydrate(snap) {
    if (!snap || snap.v !== 1) return false;
    state.guests = parseInt(snap.guests, 10) || 0;
    state.date = snap.date || '';
    state.tier = (snap.tier === 'indiv') ? 'indiv' : 'suite';
    ITEMS.forEach(function (d) {
      var s = snap.items && snap.items[d.id], it = state.items[d.id];
      if (s) { it.on = true; it.format = s.format || null; it.material = s.material || null; it.size = s.size || null; it.qty = parseInt(s.qty, 10) || 0; it.qtyManual = !!s.qtyManual; it.ribbon = s.ribbon || 'none'; it.ups = s.ups || {}; it.note = s.note || ''; it.customized = !!s.customized; }
      else { it.on = false; }
    });
    var g = $('otdGuests'); if (g) g.value = state.guests || '';
    var dd = $('otdDate'); if (dd) dd.value = state.date || '';
    buildGroups();
    if (snap.prepaid) { setPrepaid(true); var ci = $('otdCodeInput'); if (ci) ci.value = snap.code || ''; applyCode(snap.code || ''); }
    else { setTier(state.tier); }
    state.step = STEP_SELECT; renderStep(); // return to "Select your stationery pieces" so they can adjust from there
    return true;
  }
  function maybeRestore() {
    try {
      if (typeof location === 'undefined' || !/[?&]otdedit=1/.test(location.search || '')) return;
      fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (cart) {
        var line = (cart.items || []).filter(function (li) { return li.properties && (li.properties._otd_state || li.properties._scope_source === 'OTD-master'); }).pop();
        if (!line || !line.properties._otd_state) return;
        var snap; try { snap = JSON.parse(line.properties._otd_state); } catch (e) { return; }
        rehydrate(snap);
      }).catch(function () { });
    } catch (e) { }
  }
  // ── Single-piece config persistence (localStorage, keyed per piece) ─────────
  var OTD_TTL_DAYS = 30, OTD_LS_PREFIX = 'fel_otd_piece_';
  function otdSave() {
    if (!state.singlePiece) return;
    try { localStorage.setItem(OTD_LS_PREFIX + state.singlePiece, JSON.stringify({ ts: Date.now(), snap: stateSnapshot() })); } catch (e) { }
  }
  function otdLoadPiece(id) {
    try {
      var raw = localStorage.getItem(OTD_LS_PREFIX + id); if (!raw) return null;
      var obj = JSON.parse(raw); if (!obj || !obj.snap) return null;
      if ((Date.now() - obj.ts) / 86400000 > OTD_TTL_DAYS) { localStorage.removeItem(OTD_LS_PREFIX + id); return null; }
      return obj.snap;
    } catch (e) { return null; }
  }
  function restoreSinglePiece(snap) {
    if (!snap || snap.v !== 1) return;
    if (snap.guests) { state.guests = parseInt(snap.guests, 10) || 0; var g = $('otdGuests'); if (g) g.value = state.guests || ''; }
    if (snap.date) { state.date = snap.date; var dd = $('otdDate'); if (dd) dd.value = state.date; }
    var id = state.singlePiece, s = snap.items && snap.items[id], it = state.items[id];
    if (s && it) {
      it.on = true; it.format = s.format || null; it.material = s.material || null; it.size = s.size || null;
      it.qty = parseInt(s.qty, 10) || 0; it.qtyManual = !!s.qtyManual; it.ribbon = s.ribbon || 'none';
      it.ups = s.ups || {}; it.note = s.note || ''; it.customized = !!s.customized;
    }
  }
  // Bundle mode (signage / table suite product pages): a fixed subset of pieces, $150 flat design.
  var OTD_LS_BUNDLE = 'fel_otd_bundle_';
  function bundleLoad(key) {
    try {
      var raw = localStorage.getItem(OTD_LS_BUNDLE + key); if (!raw) return null;
      var obj = JSON.parse(raw); if (!obj || !obj.ts) return null;
      if ((Date.now() - obj.ts) / 86400000 > OTD_TTL_DAYS) { localStorage.removeItem(OTD_LS_BUNDLE + key); return null; }
      return obj.snap;
    } catch (e) { return null; }
  }
  function bundleSave() {
    if (!state.bundleItems || !state.bundleKey) return;
    try { localStorage.setItem(OTD_LS_BUNDLE + state.bundleKey, JSON.stringify({ ts: Date.now(), snap: stateSnapshot() })); } catch (e) { }
  }
  function detectBundle(e) {
    try {
      var raw = (e && e.dataset && e.dataset.bundleItems) ? String(e.dataset.bundleItems).trim() : '';
      if (!raw) return;
      var ids = raw.split(',').map(function (s) { return s.trim(); }).filter(function (s) { return s && defOf(s); });
      if (!ids.length) return;
      state.bundleItems = ids;
      state.bundleKey = (e.dataset.bundleKey || 'bundle').trim();
      state.bundleLabel = (e.dataset.bundleLabel || 'On-the-day bundle').trim();
      state._bundleSaved = bundleLoad(state.bundleKey); // capture BEFORE any recalc overwrites localStorage
    } catch (x) { }
  }
  function restoreBundle() {
    if (!state._bundleSaved) return;
    try {
      rehydrate(state._bundleSaved);
      // Keep the restored piece selections, but always START on the intro tab (guests + date)
      // rather than mid-flow on Select. firstRender stays true so the page doesn't auto-scroll.
      state.step = 1; firstRender = true;
    } catch (e) { }
  }
  function detectSinglePiece() {
    try {
      var e = root();
      var pid = (e && e.dataset && e.dataset.singlePiece) ? String(e.dataset.singlePiece).trim() : '';
      if (!pid && typeof location !== 'undefined') { var m = (location.search || '').match(/[?&]piece=([a-z0-9-]+)/i); if (m) pid = m[1]; }
      if (!pid || !defOf(pid)) return;
      state.singlePiece = pid;
      var saved = otdLoadPiece(pid);   // read BEFORE toggleItem (its recalc would otherwise save defaults over it)
      toggleItem(pid, true);           // smart defaults
      if (saved) restoreSinglePiece(saved); // override defaults with the saved configuration
    } catch (e) { }
  }
  function init() {
    var e = root(); if (!e) return;
    readCfg(e); detectBundle(e); buildGroups(); wire(e); setTier('suite'); syncFromInputs(); detectSinglePiece(); if (state.bundleItems) restoreBundle(); renderStep(); maybeRestore();
  }
  window.OTDMaster = { go: go, goTo: goTo, init: init, _snapshot: stateSnapshot, _rehydrate: rehydrate, _toggle: toggleItem };
  function safeInit() { try { if (root()) init(); } catch (x) { console.error('[OTDMaster] init failed', x); } }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', safeInit); else safeInit();
  document.addEventListener('shopify:section:load', function (e) { if (e.target && e.target.querySelector && e.target.querySelector('.otd-master')) safeInit(); });
  // bfcache / instant-navigation restores: re-sync state from the (restored) inputs so Continue isn't stuck.
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('pageshow', function () { try { if (root()) syncFromInputs(); } catch (x) { } });
  }
})();
